import { AsyncPipe, CommonModule, NgForOf, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, signal, computed, OnDestroy, OnInit, ChangeDetectorRef, inject, ViewChild, TemplateRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import type { ValidatorFn } from '@angular/forms';
import { FormControl, FormsModule, ReactiveFormsModule, FormGroup, FormArray, Validators } from '@angular/forms';
import { RouterLink, ActivatedRoute } from '@angular/router';
import type { TuiBooleanHandler } from '@taiga-ui/cdk';
import { TuiDay, TuiLet, TuiRepeatTimes, TuiTime, TuiContext, TuiIdentityMatcher, TuiStringHandler, TuiDayRange, TuiPlatform, tuiDefaultSort, TuiValidator } from '@taiga-ui/cdk';
import { TuiAppearance, TuiButton, TuiInitialsPipe, TuiTitle, TuiLink, TuiPopup, TuiIconPipe, TuiScrollbar, TuiError, TuiGroup, TuiLabel, TuiTextfield, TuiDataList, TuiNotification, tuiDateFormatProvider, TuiIcon, TuiFormatNumberPipe, TuiNumberFormat, TuiLoader, tuiLoaderOptionsProvider } from '@taiga-ui/core';
import type { TuiSizeS } from '@taiga-ui/core';
import type { TuiPaymentSystem } from '@taiga-ui/addon-commerce';
import { TuiThumbnailCard, TuiCurrencyPipe, TuiCurrency } from '@taiga-ui/addon-commerce';
import type { TuiComparator } from '@taiga-ui/addon-table';
import { TuiTable } from '@taiga-ui/addon-table';
import { WaIntersectionObserver } from '@ng-web-apis/intersection-observer';
import { TuiBlockStatus } from '@taiga-ui/layout';
import { TuiResponsiveDialogService } from '@taiga-ui/addon-mobile';
import type { TuiConfirmData } from '@taiga-ui/kit';
import { TUI_CONFIRM } from '@taiga-ui/kit';
import { switchMap } from 'rxjs';

import {
    TuiAvatar,
    TuiBadge,
    TuiStatus,
    TuiBadgedContent,
    TuiCheckbox,
    TuiChevron,
    TuiDataListWrapper,
    TuiFilterByInputPipe,
    TuiInputNumber,
    TuiPassword,
    TuiRadio,
    TuiSelect,
    TuiSortCountriesPipe,
    TuiStringifyContentPipe,
    TuiSwitch,
    TuiTooltip,
    tuiInputPhoneInternationalOptionsProvider,
    TuiDrawer,
    TuiTabs,
    TuiBlock,
    TuiInputInline,
    TuiTextarea,
    TuiChip,
    TuiSkeleton,
} from '@taiga-ui/kit';
import { TuiCardLarge, TuiForm, TuiHeader, TuiNavigation, TuiCell } from '@taiga-ui/layout';
import type { TuiSwipeEvent } from '@taiga-ui/cdk';
import { tuiClamp, TuiSwipe } from '@taiga-ui/cdk';
import type { TuiDialogContext } from '@taiga-ui/core';
import { TuiPreview, TuiPreviewDialogService } from '@taiga-ui/kit';
import type { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { PolymorpheusOutlet, PolymorpheusTemplate } from '@taiga-ui/polymorpheus';
import { TuiSelectModule, TuiInputDateModule, TuiInputModule, TuiInputPhoneModule, TuiInputSliderModule, TuiInputTimeModule, TuiTextfieldControllerModule, TuiMultiSelectModule, TuiInputDateRangeModule, TuiTextareaModule } from '@taiga-ui/legacy';
import { delay, of, defer, Subscription, forkJoin } from 'rxjs';
import { TuiFiles, TuiFile, TuiFieldErrorPipe, TuiInputFiles, TuiInputFilesValidator, TuiButtonClose } from '@taiga-ui/kit';
import { map, catchError } from 'rxjs/operators';
import type { TuiCountryIsoCode } from '@taiga-ui/i18n';
import { getCountries } from 'libphonenumber-js';
import { CurrencyService } from '../../../shared/services/currency.service';
import { VehicleIconsService } from '../../../shared/services/vehicle-icons.service';
import { ApiService, readServiceType, serviceTypeMatches } from '../../../shared/services/api.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { FacilityDrawer } from '../../../shared/components/drawers/facility-drawer/facility-drawer';
import { PermissionDisableDirective } from '../../../shared/directives/permission-disable.directive';
import { SetupGuideService } from '../../../shared/services/setup-guide.service';

// User class for facility type management
class User {
    constructor(
        public readonly name: string,
        public readonly icon: string | null = null,
    ) { }

    public toString(): string {
        return this.name;
    }
}

// API services data - will be populated from API
let apiServicesData: ApiServiceData[] = [];

// Interface for facility type
interface FacilityType {
    name: string;
    unit_type_id?: string; // Optional unit type ID
}

// Interface for pricing type configuration
interface PricingType {
    code: string;
    label: string;
    calculation_type: string;
    unit_label: string;
    description: string;
}

// Interface for API service data
interface ApiServiceData {
    id: string;
    name: string;
    key: string;
    description: string;
    service_type: string;
    icon_url: string | null;
    icon_color: string | null;
    bg_color_light: string | null;
    bg_color_dark: string | null;
    exisitng_service_id: string | null;
    service_id?: string; // Add optional service_id for filtering
    service_tax_id: string;
    capacity: number;
    media_assets: string[];
    org_id: string;
    parking_id: string | null;
    status: boolean;
    createdAt: string;
    updatedAt: string;
    unit_type_id?: string; // Optional unit type ID from API
}

import { FacilityIconStylePipe, ContainerStatPipe } from '../../../shared/pipes/fast-rendering.pipes';

@Component({
    standalone: true,
    exportAs: "settings",
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TuiAppearance,
        TuiAvatar,
        TuiButton,
        TuiChevron,
        TuiForm,
        TuiHeader,
        TuiNavigation,
        TuiRepeatTimes,
        TuiDataListWrapper,
        TuiSelectModule,
        TuiCardLarge,
        TuiSwitch,
        TuiBadge,
        TuiBadgedContent,
        TuiInputNumber,
        TuiRadio,
        TuiSelect,
        TuiCell,
        TuiTitle,
        TuiScrollbar,
        TuiTextfield,
        TuiDataList,
        TuiTabs,
        TuiInputDateModule,
        TuiInputModule,
        TuiInputPhoneModule,
        TuiInputSliderModule,
        TuiInputTimeModule,
        TuiTextfieldControllerModule,
        TuiMultiSelectModule,
        TuiInputDateRangeModule,
        TuiTextareaModule,
        TuiThumbnailCard,
        TuiTable,
        TuiNumberFormat,
        WaIntersectionObserver,
        TuiChip,
        TuiIcon,
        TuiBlockStatus,
        TuiLoader,
        TuiSkeleton,
        FacilityDrawer,
        TuiStatus,
        TuiPreview,
        PolymorpheusOutlet,
        PolymorpheusTemplate,
        TuiSwipe,
        TuiFiles,
        TuiFile,
        TuiFieldErrorPipe,
        TuiButtonClose,
        TuiInputFiles,
        TuiInputFilesValidator,
        TuiNotification,
        PermissionDisableDirective,
        FacilityIconStylePipe,
        ContainerStatPipe
    ],
    templateUrl: './facilities.html',
    styleUrl: './facilities.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [tuiLoaderOptionsProvider({ size: 'xl' })],
})
export default class Facilities implements OnInit, OnDestroy {
    private servicesSubscription?: Subscription;
    // UI State Management
    protected expandedSettings = signal(true);
    protected open = false;
    protected switch = true;
    protected readonly routes: any = {};
    protected readonly breadcrumbs: string[] = [];

    // Image preview properties
    protected previewIndex = 0;
    protected previewContainer: any = null;

    @ViewChild('preview')
    protected readonly preview?: TemplateRef<TuiDialogContext>;

    @ViewChild('uploadDialog')
    protected readonly uploadDialog?: TemplateRef<TuiDialogContext>;

    @ViewChild('facilityDrawer')
    protected facilityDrawer?: FacilityDrawer;

    // Property to hold facility data for editing
    protected facilityDataForEdit: any = null;

    // Currency and locale settings
    protected currentCurrency = '';
    protected currentLocale = 'en-IN';
    protected isRTL = false;

    // Vehicle containers array to store multiple vehicle configurations - converted to signal
    protected vehicleContainers = signal<any[]>([]);

    // API data properties
    protected parkingId: string = ''; // Will be set dynamically from user's parking
    protected rateSlotsData: any = {};
    protected isLoading = signal(false);
    protected errorMessage = signal('');
    protected successMessage = signal('');
    protected isSaving = false;
    protected saveMessage = '';
    protected savingRowId = signal<number | null>(null);
    protected deletingRowId = signal<number | null>(null);
    protected isUpdatingSession: Record<string, boolean> = {};

    // Facility selection and management
    private readonly route = inject(ActivatedRoute);
    protected selectedUser: User | null = null;
    protected availableFacilities = signal<User[]>([]);
    protected readonly items$ = this.availableFacilities.asReadonly();
    protected readonly disabledItemHandler: TuiBooleanHandler<User> = ({ icon }) => !!icon;
    protected selected: readonly User[] = [];
    protected user: User | null = null;

    // API services data
    protected apiServicesData: ApiServiceData[] = [];
    protected isLoadingServices = signal(false);
    protected servicesErrorMessage = signal('');
    protected vehicleServices: string[] = [];

    // Parking detail facilities data
    protected parkingDetailFacilities: any[] = [];
    protected isLoadingParkingDetail = signal(false);

    // Facility types for selection - will be populated from API
    protected faclitiesTypes: FacilityType[] = [];

    // Pricing types configuration - will be loaded from API
    protected pricingTypes: PricingType[] = [];

    // Unit types from API
    protected apiUnitTypes: any[] = [];
    protected isLoadingUnits = signal(false);
    protected selectedUnit: any = null;
    protected unitTypeDetails: any = null; // Store unit type details from API

    protected selectedCard: FacilityType | null = null;
    protected stringify: TuiStringHandler<FacilityType> = (x) => x.name;
    protected selectedRegion: any = null;
    protected readonly selectedTypeValue = signal<FacilityType | null>(null);
    protected readonly isAddTypeDisabled = computed(() => this.isLoading() || !this.selectedTypeValue());

    // Form for facility selection validation
    protected vehicleSelectionForm = new FormGroup({
        selectedCard: new FormControl<FacilityType | null>(null, [Validators.required])
    });

    // Table properties
    protected readonly columns = ['duration', 'minHours', 'maxHours', 'price', 'withTax', 'description', 'actions'] as const;
    protected readonly totalSorter: TuiComparator<any> = (a, b) => a.price - b.price;
    protected readonly minPrice = (value: number) => value < 0 ? 'Price must be positive' : null;
    protected readonly options = { standalone: true };
    // Units and unit types - will be loaded from API
    protected units: string[] = [];
    protected unitTypes: string[] = ['Hourly', 'Flat'];

    // Unit type options for Time Based pricing
    protected readonly timeBasedUnitTypes = ['Hourly', 'Flat'];

    // Dynamic columns based on pricing type
    protected getDynamicColumns(pricingType: string): string[] {
        switch (pricingType) {
            case 'Time Based':
            case 'Hour Based':
                return ['icon', 'duration', 'minHours', 'maxHours', 'price', 'withTax', 'description', /* 'unitType', */ 'unit', 'actions'];
            case 'Flat Rate':
                return ['icon', 'tierName', 'price', 'withTax', 'description', 'unit', 'actions'];
            case 'Per Unit':
            case 'Energy Consumption':
            case 'Per Day':
                return ['icon', 'tierName', 'price', 'withTax', 'available', 'description', 'unit', 'actions'];
            default:
                return ['icon', 'duration', 'minHours', 'maxHours', 'price', 'withTax', 'description', /* 'unitType', */ 'unit', 'actions'];
        }
    }

    // Get table columns for a specific container
    protected getContainerColumns(container: any): string[] {
        if (!container.pricingType) {
            return [];
        }
        return this.getDynamicColumns(container.pricingType);
    }

    // Get image URL for uploaded files
    protected getImageUrl(file: any): string {
        if (file && file.src) {
            return file.src;
        }
        if (file && file.url) {
            return file.url;
        }
        if (file && typeof file === 'string') {
            return file;
        }
        if (file && file instanceof File) {
            return URL.createObjectURL(file);
        }
        // Fallback to a default image
        return 'assets/images/icons/u2.svg';
    }

    private readonly dialogs = inject(TuiResponsiveDialogService);
    private readonly notificationService = inject(NotificationService);
    private readonly previewService = inject(TuiPreviewDialogService);
    public readonly sanitizer = inject(DomSanitizer);

    // File upload properties for facility images
    protected readonly maxImageSizeBytes = 2 * 1024 * 1024; // 2MB per image
    protected currentFacilityContainer: any = null;
    protected readonly fileControl = new FormControl<File[]>([], [
        this.maxFilesLength(5),
        this.maxFileSize(this.maxImageSizeBytes),
    ]);
    protected readonly accepted$ = this.fileControl.valueChanges.pipe(
        map(files => files?.filter(file => file instanceof File) || [])
    );
    protected rejected: readonly File[] = [];
    protected existingImages: string[] = [];
    protected uploadErrorMessage = '';
    protected isUploadingImages = false;
    protected fileUrlCache = new Map<File, string>();

    // Cache for unit type labels
    private unitTypeIdToLabel = new Map<string, string>();

    constructor(
        public currencyService: CurrencyService,
        private vehicleIconsService: VehicleIconsService,
        private apiService: ApiService,
        private cdr: ChangeDetectorRef,
        private setupGuideService: SetupGuideService
    ) {
        // Subscribe to region changes to automatically update currency
        this.currencyService.currentRegion$.subscribe(region => {
            if (region) {
                this.currentCurrency = region.currency_code;
                this.currentLocale = region.locale_code;
                this.isRTL = region.text_direction === 'rtl';
                // Use setTimeout to avoid change detection error
                setTimeout(() => {
                    this.cdr.detectChanges();
                });
            }
        });

        // Subscribe to form control changes to keep selectedCard in sync
        this.vehicleSelectionForm.controls.selectedCard.valueChanges.subscribe(value => {
            this.selectedCard = value;
            this.selectedTypeValue.set(value);
            this.cdr.markForCheck();
        });

        // Preload unit type labels for mapping unit_type_id -> name
        this.loadUnitTypeLabels();

        // Reject oversized images (>2MB) before they stay in the control
        this.fileControl.valueChanges.subscribe(files => {
            if (!files?.length) {
                return;
            }

            const oversized = files.filter(file => file.size > this.maxImageSizeBytes);
            if (oversized.length === 0) {
                return;
            }

            this.rejected = [...this.rejected, ...oversized];
            this.uploadErrorMessage =
                'Each image must be 2MB or smaller. Please compress the image or choose a smaller file.';
            this.fileControl.setValue(
                files.filter(file => file.size <= this.maxImageSizeBytes),
                { emitEvent: false }
            );
            this.cdr.markForCheck();
        });
    }

    private loadUnitTypeLabels(): void {
        this.apiService.getUnits().subscribe({
            next: (response: any) => {
                let list: any[] = [];
                if (Array.isArray(response)) list = response;
                else if (response?.data && Array.isArray(response.data)) list = response.data;
                for (const u of list) {
                    const id = u?.id;
                    const label = u?.label || u?.name || u?.unit_label || '';
                    if (id && label) this.unitTypeIdToLabel.set(id, label);
                }
            },
            error: () => { }
        });
    }

    protected getPricingTypeFromUnitTypeId(unitTypeId: string): string {
        // Map unit type IDs to pricing types based on the API response
        switch (unitTypeId) {
            case 'a0b90dd5-e8b8-412a-902b-517b34ffd13f': // time_based
                return 'Time Based';
            case '994acb09-b529-4cf2-a353-eddc3992cc83': // hourly
                return 'Hour Based';
            case '6174486a-7bf5-4ca0-a065-f15ab0864167': // unit
                return 'Per Unit';
            case '55bd4dd9-6d88-4f08-abc6-8adeb55db82b': // fixed
                return 'Flat Rate';
            case '1300d745-b863-4a99-94cd-9a600fe8566b': // energy
                return 'Energy Consumption';
            case '0d47a2b3-b222-451a-b83a-ae31241b0d22': // day
                return 'Per Day';
            default:
                return 'Time Based';
        }
    }

    /**
     * Pricing type shown on the facility card.
     * Parking services keep the catalog unit_type_id (Helmet Locker stays the Flat Rate id)
     * even after edit. The updated type is on unitType.
     */
    protected resolveFacilityPricingType(facilityData: any): string {
        const unit = facilityData?.unitType || facilityData?.unit_type || facilityData?.unit;
        if (unit && typeof unit === 'object') {
            const label = String(unit.label || unit.name || unit.title || '').trim();
            if (label) return label;
            if (unit.id) return this.getPricingTypeFromUnitTypeId(unit.id);
        }
        if (typeof unit === 'string' && unit.trim()) return unit.trim();
        if (facilityData?.unit_type_id) {
            return this.getPricingTypeFromUnitTypeId(facilityData.unit_type_id);
        }
        if (facilityData?.pricingType) return facilityData.pricingType;
        return 'Time Based';
    }

    /**
     * Initialize component and load vehicle rate slots
     */
    ngOnInit(): void {
        // Get parking ID from logged-in user's data
        this.initializeParkingId();

        // Set initial region
        const currentRegion = this.currencyService.getCurrentRegion();
        if (currentRegion) {
            this.selectedRegion = currentRegion;
        }

        // Initialize form control with selectedCard value
        if (this.selectedCard) {
            setTimeout(() => {
                this.vehicleSelectionForm.controls.selectedCard.setValue(this.selectedCard);
            });
        }

        // Load services from API (this will populate the dropdown)
        this.loadServices();

        // Load other data after services
        this.loadFacilityRateSlots();
        this.loadUnits();
        this.loadPricingTypes();

        // Auto-refresh on service mutations (create/update/delete/media)
        this.servicesSubscription = this.apiService.servicesChanged$.subscribe(() => {
            this.loadServices();
            this.loadFacilityRateSlots();
        });

        // Load parking list for Valet Parking dropdown
        this.loadValetParkingOptions();
    }

    ngOnDestroy(): void {
        if (this.servicesSubscription) {
            this.servicesSubscription.unsubscribe();
        }
        if (this.facilityRefreshTimer) {
            clearTimeout(this.facilityRefreshTimer);
        }
    }

    /**
     * Initialize parking ID from route params or logged-in user's data
     */
    private initializeParkingId(): void {
        // Try to get from route params first
        let id = this.route.snapshot.params['id'] ||
            this.route.snapshot.parent?.params['id'] ||
            this.route.snapshot.params['id'] || // duplicated check but safe
            this.route.snapshot.paramMap.get('id');

        // Fallback: extract from URL
        if (!id) {
            const pathSegments = window.location.pathname.split('/');
            const parkingIdx = pathSegments.indexOf('parking');
            if (parkingIdx !== -1 && pathSegments[parkingIdx + 1]) {
                id = pathSegments[parkingIdx + 1];
            }
        }

        if (id && id !== '9e22be24-5c36-47f0-a91d-6920e7d3bc7e') {
            this.parkingId = id;
            sessionStorage.setItem('parking_id', this.parkingId);
            return;
        }

        // First try to get parking ID from localStorage
        const parkingIdFromStorage = sessionStorage.getItem('parking_id');
        if (parkingIdFromStorage && parkingIdFromStorage !== '9e22be24-5c36-47f0-a91d-6920e7d3bc7e') {
            this.parkingId = parkingIdFromStorage;
        } else {
            // Fallback to user parkings if localStorage doesn't have parking_id
            const userParkings = this.apiService.getUserParkings();
            if (userParkings && userParkings.length > 0) {
                const validParkings = userParkings.filter((p: any) => p?.id && p.id !== '9e22be24-5c36-47f0-a91d-6920e7d3bc7e');
                if (validParkings.length > 0) {
                    this.parkingId = validParkings[0].id;
                    sessionStorage.setItem('parking_id', this.parkingId);
                }
            } else {
                this.errorMessage.set('No parking ID found in localStorage or user data');
            }
        }
    }

    /**
     * Load services from API
     */
    protected loadServices(): void {
        this.isLoadingServices.set(true);
        this.servicesErrorMessage.set('');

        // Use the existing services endpoint to get all services
        this.apiService.getExistingServices().subscribe({
            next: (response: any) => {

                // Handle different response formats
                let servicesData: any[] = [];

                if (response && Array.isArray(response)) {
                    // Direct array response
                    servicesData = response;
                } else if (response && response.data && Array.isArray(response.data)) {
                    // Standard API response format
                    servicesData = response.data;
                } else if (response && response.success && response.data && Array.isArray(response.data)) {
                    // Success wrapper format
                    servicesData = response.data;
                } else {
                    servicesData = [];
                    this.servicesErrorMessage.set('No services data available');
                }

                // Store all services data
                this.apiServicesData = servicesData;

                // Update facility types from API data
                this.updateFacilityTypesFromApi();

                this.isLoadingServices.set(false);
            },
            error: (error: any) => {
                this.apiServicesData = [];
                this.servicesErrorMessage.set('Failed to load services from API');
                this.isLoadingServices.set(false);
            }
        });
    }

    /**
     * Load parking list for Valet Parking dropdown
     */
    protected loadValetParkingOptions(): void {
        const orgId = this.apiService.getCurrentOrganizationId();
        if (!orgId) return;

        this.apiService.getParkingListByOrg(orgId).subscribe({
            next: (response: any) => {
                let list: any[] = [];
                if (Array.isArray(response)) {
                    list = response;
                } else if (response?.data && Array.isArray(response.data)) {
                    list = response.data;
                } else if (response?.parkings && Array.isArray(response.parkings)) {
                    list = response.parkings;
                }

                // Find current parking
                const currentParking = list.find((p: any) => p.id === this.parkingId);

                if (currentParking) {
                    // Extract vehicles from the parking object
                    // Based on user provided response, use 'vehicleTypes'
                    const vehicles = currentParking.vehicleTypes || currentParking.vehicles || [];

                    if (Array.isArray(vehicles)) {
                        this.vehicleServices = vehicles.map((v: any) => v.name || v.type || 'Unknown');
                    }
                }

                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Failed to load valet parking options', err);
            }
        });
    }

    /**
     * Returns vehicle service options available for selection in a valet container row,
     * excluding vehicle services that have already been configured in other rows of the same container.
     */
    protected getAvailableVehicleServices(container: any, currentRow?: any): string[] {
        if (!this.vehicleServices || !this.vehicleServices.length) {
            return [];
        }
        if (!container || !Array.isArray(container.tableRows)) {
            return this.vehicleServices;
        }

        // Collect names of all vehicles selected in other rows in this container
        const usedVehicles = new Set<string>();
        for (const row of container.tableRows) {
            if (row && row !== currentRow) {
                const name = (row.tierName || row.duration || row.name || '').trim().toLowerCase();
                if (name) {
                    usedVehicles.add(name);
                }
            }
        }

        // Return only services not already added/selected
        return this.vehicleServices.filter(v => {
            const vLower = (v || '').trim().toLowerCase();
            return !usedVehicles.has(vLower);
        });
    }

    /**
     * Update facility types from API data
     */
    protected updateFacilityTypesFromApi(): void {

        // Filter services to include facility services from existing services API
        let facilityServices = this.apiServicesData.filter(service =>
            serviceTypeMatches(service, 'facility')
        );

        // Filter based on service_access if restricted
        const serviceAccess = this.apiService.getServiceAccess();
        const user = this.apiService.getCurrentUser();
        const currentRole = (user?.current_role || user?.role?.name || user?.type || sessionStorage.getItem('current_role') || '').toLowerCase().trim();
        const isSuperOrAdmin = currentRole === 'super admin' || currentRole === 'admin' || currentRole === 'super_admin' || currentRole === 'vendor' || user?.email === 'system_admin@kormoan.com';

        if (!isSuperOrAdmin && serviceAccess && serviceAccess.facility !== undefined) {
            const hasFacilityAccess = serviceAccess.facility !== false &&
                (Array.isArray(serviceAccess.facility) ? serviceAccess.facility.length > 0 : !!serviceAccess.facility);

            if (!hasFacilityAccess) {
                facilityServices = [];
            } else if (Array.isArray(serviceAccess.facility)) {
                const isAll = serviceAccess.facility.includes('all') || serviceAccess.facility.includes('*');
                if (!isAll) {
                    facilityServices = facilityServices.filter(service =>
                        serviceAccess.facility.includes(service.id) ||
                        (service.service_id && serviceAccess.facility.includes(service.service_id)) ||
                        (service.exisitng_service_id && serviceAccess.facility.includes(service.exisitng_service_id)) ||
                        (service.name && serviceAccess.facility.includes(service.name))
                    );
                }
            }
        }

        // Always update facility types from API data (override parking detail data)
        // Convert filtered API services to facility types
        this.faclitiesTypes = facilityServices.map(service => ({
            name: service.name,
            unit_type_id: service.unit_type_id || undefined
        }));

        // Update available facilities with filtered services
        const availableFacilityUsers = facilityServices.map(service =>
            new User(service.name, service.icon_url)
        );
        this.availableFacilities.set(availableFacilityUsers);


        // Force change detection
        setTimeout(() => {
            this.cdr.detectChanges();
        });
    }

    /**
     * Get filtered facility types that are not already in the list
     */
    protected getFilteredFacilitiesTypes(): FacilityType[] {
        const addedNames = this.vehicleContainers().map(c => c.facilityType.toLowerCase());
        // Facility types hidden from the dropdown
        const hiddenTypes = ['ev charging station', 'restrooms access', 'restroom access'];
        return this.faclitiesTypes
            .filter(f => {
                const lowerName = f.name.toLowerCase();
                if (hiddenTypes.includes(lowerName)) return false;
                const normalizedName = this.formatFacilityName(f.name).toLowerCase();
                return !addedNames.includes(normalizedName) && !addedNames.includes(lowerName);
            })
            .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    }

    /**
     * Load facility rate slots from API using the new bulk endpoint
     */
    protected loadFacilityRateSlots(): void {
        if (!this.parkingId) {
            this.errorMessage.set('No parking ID available');
            this.isLoading.set(false);
            return;
        }

        this.isLoading.set(true);
        this.errorMessage.set('');

        if (this.vehicleContainers().length === 0) {
            this.vehicleContainers.set([
                {
                    id: 'skeleton-container-1',
                    facilityType: 'Facility Service Type',
                    pricingType: 'Time Based',
                    isActive: true,
                    description: 'Configure standard pricing tiers and access rules for this facility.',
                    tableRows: [
                        { id: 'sk-1', duration: 'First 2 Hours', minHours: '0', maxHours: '2', price: 10, description: 'Standard base hourly rate tier', unitType: 'Hourly' },
                        { id: 'sk-2', duration: 'Next 4 Hours', minHours: '2', maxHours: '6', price: 20, description: 'Extended parking duration fee', unitType: 'Hourly' },
                        { id: 'sk-3', duration: 'Over 6 Hours', minHours: '6', maxHours: '24', price: 35, description: 'Daily maximum storage charge', unitType: 'Hourly' }
                    ]
                }
            ]);
        }

        // Using the new getParkingServices endpoint for facilities
        this.apiService.getParkingServices(this.parkingId, { service_type: 'facility' }).subscribe({
            next: (response) => {
                this.rateSlotsData = response;

                // response data structure: { parking: {...}, services: [...] }
                let servicesArray = response?.data?.services || response?.services || [];
                if (response && Array.isArray(response)) {
                    servicesArray = response;
                }

                // Filter services based on service_access if restricted
                const serviceAccess = this.apiService.getServiceAccess();
                const user = this.apiService.getCurrentUser();
                const currentRole = (user?.current_role || user?.role?.name || user?.type || sessionStorage.getItem('current_role') || '').toLowerCase().trim();
                const isSuperOrAdmin = currentRole === 'super admin' || currentRole === 'admin' || currentRole === 'super_admin' || currentRole === 'vendor' || user?.email === 'system_admin@kormoan.com';

                if (!isSuperOrAdmin && serviceAccess && serviceAccess.facility !== undefined) {
                    const hasFacilityAccess = serviceAccess.facility !== false &&
                        (Array.isArray(serviceAccess.facility) ? serviceAccess.facility.length > 0 : !!serviceAccess.facility);

                    if (!hasFacilityAccess) {
                        servicesArray = [];
                    } else if (Array.isArray(serviceAccess.facility)) {
                        const isAll = serviceAccess.facility.includes('all') || serviceAccess.facility.includes('*');
                        if (!isAll) {
                            servicesArray = servicesArray.filter((s: any) =>
                                serviceAccess.facility.includes(s.id) ||
                                (s.service_id && serviceAccess.facility.includes(s.service_id)) ||
                                (s.exisitng_service_id && serviceAccess.facility.includes(s.exisitng_service_id)) ||
                                (s.name && serviceAccess.facility.includes(s.name))
                            );
                        }
                    }
                }

                // Always create a new list so deleted services are removed
                const finalContainers: any[] = [];
                const currentContainers = this.vehicleContainers().filter(c => c.id !== 'skeleton-container-1');

                if (servicesArray.length > 0) {
                    servicesArray.forEach((serviceData: any, index: number) => {
                        const serviceType = readServiceType(serviceData);
                        if (serviceType && !serviceTypeMatches(serviceData, 'facility')) {
                            return;
                        }

                        // Determine facility name
                        let facilityName = serviceData.name;
                        if (!facilityName) {
                            facilityName = `Facility ${serviceData.id?.substring(0, 8)}`;
                        }

                        // Check if it already existed to preserve its ID, else assign a new ID
                        const existingContainer = currentContainers.find(c => c.facilityType === this.formatFacilityName(facilityName));
                        const containerId = existingContainer ? existingContainer.id : finalContainers.length + 1;

                        const newContainer = this.createFacilityContainerFromApiData(serviceData, facilityName, containerId);
                        finalContainers.push(newContainer);
                    });
                }

                // Set the containers sorted by created_at and update available facilities
                const sortedContainers = this.sortContainersByCreatedAt(finalContainers);
                this.vehicleContainers.set(sortedContainers);
                sortedContainers.forEach(container => {
                    this.removeFacilityFromAvailableList(container.facilityType);
                });

                this.isLoading.set(false);
                this.cdr.detectChanges();
            },
            error: (error) => {
                console.error('Failed to load facility services:', error);
                this.errorMessage.set('Could not load facilities.');
                this.isLoading.set(false);
                if (this.vehicleContainers().some(c => c.id === 'skeleton-container-1')) {
                    this.vehicleContainers.set([]);
                }
                this.cdr.detectChanges();
            }
        });
    }

    /**
     * Process the rate slots data and populate facility containers
     */
    protected processRateSlotsData(): void {
        if (!this.rateSlotsData || typeof this.rateSlotsData !== 'object') {
            return;
        }


        // Start with existing containers instead of clearing them
        const existingContainers = [...this.vehicleContainers()];
        const newContainers: any[] = [];

        // Get facility unit type mapping from parking data if available
        const facilityUnitTypeMap = this.rateSlotsData.facilityUnitTypeMap;

        // Handle the actual API response structure
        if (this.rateSlotsData.facilities && Array.isArray(this.rateSlotsData.facilities) && this.rateSlotsData.facilities.length > 0) {
            // Process existing facility rate slots
            this.rateSlotsData.facilities.forEach((facilityGroup: any, groupIndex: number) => {
                // Each facility group contains facility objects with facility names as keys
                const facilityKeys = Object.keys(facilityGroup);

                facilityKeys.forEach(facilityKey => {
                    const facilityData = facilityGroup[facilityKey];

                    if (facilityData && facilityData.rates && Array.isArray(facilityData.rates)) {
                        // Get unit_type_id for this specific facility from the mapping
                        const facilityName = this.formatFacilityName(facilityKey);
                        const unitTypeId = facilityUnitTypeMap?.get(facilityName);
                        facilityData.unit_type_id = unitTypeId; // Pass unit_type_id to facilityData

                        // Check if container already exists for this facility
                        const existingContainerIndex = existingContainers.findIndex(container =>
                            container.facilityType === facilityName
                        );

                        if (existingContainerIndex === -1) {
                            const newContainer = this.createFacilityContainerFromApiData(facilityData, facilityKey, existingContainers.length + newContainers.length + 1);
                            newContainers.push(newContainer);
                        } else {
                            // Update existing container with fresh API data
                            const updatedContainer = this.createFacilityContainerFromApiData(facilityData, facilityKey, existingContainers[existingContainerIndex].id);
                            existingContainers[existingContainerIndex] = updatedContainer;
                        }
                    }
                });
            });
        } else {
        }

        // Combine existing containers with new containers sorted by created_at
        const finalContainers = this.sortContainersByCreatedAt([...existingContainers, ...newContainers]);

        // Update the signal with combined containers
        this.vehicleContainers.set(finalContainers);

        // Remove facility types that are already loaded from API from available lists
        finalContainers.forEach(container => {
            this.removeFacilityFromAvailableList(container.facilityType);
        });

    }

    /**
     * Helper to sort facility containers by created_at date (oldest first / chronological order)
     */
    protected sortContainersByCreatedAt(containers: any[]): any[] {
        if (!containers || !Array.isArray(containers)) return [];
        return [...containers].sort((a: any, b: any) => {
            const dateA = a.created_at || a.createdAt || a.serviceData?.created_at || a.serviceData?.createdAt || a.startDate || a.serviceData?.effective_start || null;
            const dateB = b.created_at || b.createdAt || b.serviceData?.created_at || b.serviceData?.createdAt || b.startDate || b.serviceData?.effective_start || null;

            const timeA = dateA ? new Date(dateA).getTime() : 0;
            const timeB = dateB ? new Date(dateB).getTime() : 0;

            if (timeA && timeB && timeA !== timeB) {
                return timeA - timeB;
            }
            if (timeA && !timeB) return -1;
            if (!timeA && timeB) return 1;

            return (a.id || 0) - (b.id || 0);
        });
    }

    /**
     * Create facility container from API data
     */
    protected createFacilityContainerFromApiData(facilityData: any, facilityKey: string, containerId: number): any {
        // Handle actual API response format
        const rates = facilityData.service_rates || facilityData.rates || [];
        const rateType = facilityData.rateType;

        const pricingType = this.resolveFacilityPricingType(facilityData);

        const newContainer: any = {
            id: containerId,
            facilityType: this.formatFacilityName(facilityKey),
            facilityTypeId: facilityData.id || facilityKey,
            priceId: null, // Store the priceId if available
            isActive: facilityData.status !== false && facilityData.status !== 'inactive',
            is_session_based: this.isUpdatingSession[facilityData.id || facilityKey]
                ? (this.vehicleContainers().find(c => c.facilityTypeId === (facilityData.id || facilityKey))?.is_session_based ?? (facilityData.is_session_based || false))
                : (facilityData.is_session_based || false),
            pricingType: pricingType,
            uploadedImages: facilityData.media_assets || facilityData.images || facilityData.mediaUrls || [], // Populate from API media_assets or fallbacks
            tableRows: [],
            service_taxes: facilityData.service_taxes || [], // Include service taxes for tax calculation
            description: facilityData.description,
            location: facilityData.location,
            capacity: facilityData.capacity,
            startDate: facilityData.effective_start,
            endDate: facilityData.effective_end,
            created_at: facilityData.created_at || facilityData.createdAt || null,
            createdAt: facilityData.createdAt || facilityData.created_at || null,
            serviceData: facilityData
        };

        // Convert API rates to table rows
        if (rates && rates.length > 0) {
            rates.forEach((rate: any, index: number) => {
                const price = Number(rate.price !== undefined ? rate.price : rate.rate) || 0;
                const taxPercent = Number(rate.tax || 0);
                const taxAmount = price * (taxPercent / 100);
                const withTax = price + taxAmount;

                // Determine status based on rate status
                const status = (rate.status === 'active' || rate.status === true) ? 'Active' : 'Inactive';

                // Fields
                const start = rate.min_duration !== undefined ? rate.min_duration : rate.startHour;
                const end = rate.max_duration !== undefined ? rate.max_duration : rate.endHour;

                const duration = rate.tier_name ? String(rate.tier_name).trim() : `${start || 0}h - ${end || 0}h`;
                const minHours = `${start || 0}h`;
                const maxHours = `${end || 0}h`;

                const row = {
                    id: index + 1,
                    duration: duration,
                    minHours: minHours,
                    maxHours: maxHours,
                    price: price,
                    taxAmount: taxAmount,
                    taxPercent: taxPercent,
                    withTax: Number(withTax.toFixed(2)),
                    type: rate.unitLabel || status,
                    unit: rate.unitLabel || status,
                    unitType: rate.unitLabel || 'Hourly',
                    description: rate.description || '',
                    isEditMode: false,
                    rateId: rate.id, // Store the rate ID
                    form: new FormGroup({
                        durationField: new FormControl({ value: duration, disabled: true }),
                        minHoursField: new FormControl({ value: minHours, disabled: true }),
                        maxHoursField: new FormControl({ value: maxHours, disabled: true }),
                        priceField: new FormControl({ value: price, disabled: true }),
                        withTaxField: new FormControl({ value: Number(withTax.toFixed(2)), disabled: true }),
                        typeField: new FormControl({ value: rate.unitLabel || status, disabled: true })
                    })
                };
                newContainer.tableRows.push(row);
            });
        }

        return newContainer;
    }

    /**
     * Create a default table row based on pricing type
     */
    protected createDefaultTableRow(pricingType: string, rowId: number): any {
        const baseRow = {
            id: rowId,
            isEditMode: true,
            taxAmount: 0,
            taxPercent: 0,
            withTax: 0,
            type: 'Active',
            unit: 'Active'
        };

        switch (pricingType) {
            case 'Time Based':
            case 'Hour Based':
                return {
                    ...baseRow,
                    duration: '0h - 1h',
                    minHours: '0h',
                    maxHours: '1h',
                    price: 0,
                    description: '',
                    unitType: 'Hourly',
                    form: new FormGroup({
                        durationField: new FormControl({ value: '0h - 1h', disabled: true }),
                        minHoursField: new FormControl({ value: '0h', disabled: true }),
                        maxHoursField: new FormControl({ value: '1h', disabled: true }),
                        priceField: new FormControl({ value: 0, disabled: true }),
                        withTaxField: new FormControl({ value: 0, disabled: true }),
                        typeField: new FormControl({ value: 'Active', disabled: true }),
                        unitTypeField: new FormControl({ value: 'Hourly', disabled: true })
                    })
                };
            case 'Flat Rate':
                return {
                    ...baseRow,
                    tierName: 'New Tier',
                    price: 0,
                    description: '',
                    form: new FormGroup({
                        tierNameField: new FormControl({ value: 'New Tier', disabled: true }),
                        priceField: new FormControl({ value: 0, disabled: true }),
                        withTaxField: new FormControl({ value: 0, disabled: true }),
                        descriptionField: new FormControl({ value: '', disabled: true })
                    })
                };
            case 'Per Unit':
            case 'Energy Consumption':
            case 'Per Day':
                return {
                    ...baseRow,
                    tierName: 'New Tier',
                    price: 0,
                    available: '',
                    description: '',
                    form: new FormGroup({
                        tierNameField: new FormControl({ value: 'New Tier', disabled: true }),
                        priceField: new FormControl({ value: 0, disabled: true }),
                        withTaxField: new FormControl({ value: 0, disabled: true }),
                        availableField: new FormControl({ value: '', disabled: true }),
                        descriptionField: new FormControl({ value: '', disabled: true })
                    })
                };
            default:
                return {
                    ...baseRow,
                    duration: '0h - 1h',
                    minHours: '0h',
                    maxHours: '1h',
                    price: 0,
                    description: '',
                    unitType: 'Hourly',
                    form: new FormGroup({
                        durationField: new FormControl({ value: '0h - 1h', disabled: true }),
                        minHoursField: new FormControl({ value: '0h', disabled: true }),
                        maxHoursField: new FormControl({ value: '1h', disabled: true }),
                        priceField: new FormControl({ value: 0, disabled: true }),
                        withTaxField: new FormControl({ value: 0, disabled: true }),
                        typeField: new FormControl({ value: 'Active', disabled: true }),
                        unitTypeField: new FormControl({ value: 'Hourly', disabled: true })
                    })
                };
        }
    }

    /**
     * Create vehicle container from API data (kept for backward compatibility)
     */
    protected createVehicleContainerFromApiData(vehicleData: any, containerId: number): any {
        // Handle actual API response format
        const vehicle = vehicleData.vehicle;
        const rates = vehicleData.rates || [];
        const price = vehicleData.price;
        const rateType = vehicleData.rateType;

        const newContainer: any = {
            id: containerId,
            vehicleType: vehicle.vehicleType.name,
            vehicleTypeId: vehicle.vehicleType.id,
            priceId: price?.id || null, // Store the priceId if available
            isActive: vehicle.status !== false,
            uploadedImages: [], // Initialize empty array for uploaded images
            tableRows: []
        };

        // Convert API rates to table rows
        rates.forEach((rate: any, index: number) => {
            const price = rate.rate || 0;
            const taxPercent = rate.tax || 0;
            const taxAmount = price * (taxPercent / 100);
            const withTax = price + taxAmount;


            const row = {
                id: index + 1,
                duration: `${rate.startHour}h - ${rate.endHour}h`,
                minHours: `${rate.startHour}h`,
                maxHours: `${rate.endHour}h`,
                price: price,
                taxAmount: taxAmount, // Store original tax amount from API
                taxPercent: taxPercent, // Calculated percentage for display
                withTax: withTax,
                type: rateType || 'Hourly',
                unit: rateType || 'Hourly',
                isEditMode: false,
                rateId: rate.id, // Store the rate ID
                form: new FormGroup({
                    durationField: new FormControl({ value: `${rate.startHour}h - ${rate.endHour}h`, disabled: true }),
                    minHoursField: new FormControl({ value: `${rate.startHour}h`, disabled: true }),
                    maxHoursField: new FormControl({ value: `${rate.endHour}h`, disabled: true }),
                    priceField: new FormControl({ value: price, disabled: true }),
                    withTaxField: new FormControl({ value: withTax, disabled: true }),
                    typeField: new FormControl({ value: rateType || 'Hourly', disabled: true })
                })
            };
            newContainer.tableRows.push(row);
        });

        return newContainer;
    }

    /**
     * Simple save method to prevent auto-reload
     */
    protected saveAllChanges(): void {
        this.saveMessage = 'Save functionality coming soon...';
    }

    /**
 * Test API endpoint with minimal data
 */
    protected testApiEndpoint(): void {
        // Test with exact data from working application
        const exactWorkingData = {};

        // Check authentication before making API call
        if (!this.apiService.isUserAuthenticated()) {
            this.errorMessage.set('Authentication required for test. Please login again.');
            return;
        }

        // Test with exact working data
        this.apiService.updatePriceTiers(this.parkingId, exactWorkingData).subscribe({
            next: (response) => {
                this.successMessage.set('Test with exact working data - API call successful!');
                setTimeout(() => this.successMessage.set(''), 3000);
            },
            error: (error) => {
                this.errorMessage.set(`Test failed: ${error.status} - ${error.statusText}`);
            }
        });

        // Test with the correct data format based on API response
        const testFormats: any[] = [];

        // Check authentication before making API call
        if (!this.apiService.isUserAuthenticated()) {
            this.errorMessage.set('Authentication required for test. Please login again.');
            return;
        }

        // Test each format
        testFormats.forEach((format, index) => {
            setTimeout(() => {
                this.apiService.updatePriceTiers(this.parkingId, format.data).subscribe({
                    next: (response) => {
                        this.successMessage.set(`${format.name} - API call successful!`);
                        setTimeout(() => this.successMessage.set(''), 3000);
                    },
                    error: (error) => {
                        this.errorMessage.set(`${format.name} - Failed: ${error.status} - ${error.statusText}`);
                    }
                });
            }, index * 2000); // Test each format with 2 second delay
        });
    }

    /**
     * Test method to manually trigger data loading with mock data
     */
    protected testDataLoading(): void {
        // This method is deprecated - use API data instead
        this.loadServices();
    }

    // UI Event Handlers
    protected handleToggle(): void {
        this.expandedSettings.update((e) => !e);
    }

    // Status Text Methods for UI Display
    /**
     * Returns status text for tax preview toggle
     */
    protected statusText(): string {
        return this.switch ? 'Yes' : 'No';
    }

    /**
     * Returns detailed status text for tax preview toggle
     */
    protected statusTextYesNo(): string {
        return this.switch ? 'Display price with GST (8.5%) included' : '';
    }

    // Vehicle Management Methods
    /**
     * Adds a new facility type to the containers
     */
    protected addVehicleType(): void {
        if (this.selectedUser) {
            const selectedFacility = this.selectedUser.toString();

            // Check if facility type already exists
            const existingFacility = this.vehicleContainers().find(container =>
                container.facilityType === selectedFacility
            );

            if (!existingFacility) {
                const newContainerId = this.vehicleContainers().length + 1;

                // Find the facility type from faclitiesTypes to get the unit type ID
                const facilityTypeData = this.faclitiesTypes.find(ft => ft.name === selectedFacility);
                const unitTypeId = facilityTypeData?.unit_type_id;

                // Also check if we have facility unit type mapping from rate slots data
                let mappedUnitTypeId = unitTypeId;
                if (!mappedUnitTypeId && this.rateSlotsData?.facilityUnitTypeMap) {
                    mappedUnitTypeId = this.rateSlotsData.facilityUnitTypeMap.get(selectedFacility);
                }

                // Determine pricing type based on unit type ID if available
                let pricingType = null;
                if (mappedUnitTypeId) {
                    pricingType = this.getPricingTypeFromUnitTypeId(mappedUnitTypeId);
                } else {
                    // Fallback to a default pricing type to ensure container is created
                    pricingType = 'Time Based';
                }

                const newContainer = {
                    id: newContainerId,
                    facilityType: selectedFacility,
                    isActive: true,
                    pricingType: pricingType,
                    tableRows: [],
                    service_taxes: [] // Initialize empty service taxes
                };

                this.vehicleContainers.update(containers => [...containers, newContainer]);
                this.removeFacilityFromAvailableList(selectedFacility);
                // Also remove from faclitiesTypes for consistency
                this.faclitiesTypes = this.faclitiesTypes.filter(card => card.name !== selectedFacility);
                this.selectedUser = null; // Reset selection

                // Load rates for the new container
                setTimeout(() => {
                    // this.loadAllServiceRatesForContainers();
                }, 100);
            }
        }
    }

    /**
     * Removes facility from available list when added to containers
     */
    protected removeFacilityFromAvailableList(facilityType: string): void {
        this.availableFacilities.update(facilities => {
            const filteredFacilities = facilities.filter(facility => facility.toString() !== facilityType);
            return filteredFacilities;
        });
        // Also remove from faclitiesTypes for consistency
        this.faclitiesTypes = this.faclitiesTypes.filter(card => card.name !== facilityType);
    }

    /**
     * Removes vehicle from available list when added to containers (kept for backward compatibility)
     */
    protected removeVehicleFromAvailableList(vehicleType: string): void {
        this.availableFacilities.update(facilities => {
            const filteredFacilities = facilities.filter(facility => facility.toString() !== vehicleType);
            return filteredFacilities;
        });
        // Also remove from faclitiesTypes for consistency
        this.faclitiesTypes = this.faclitiesTypes.filter(card => card.name !== vehicleType);
    }

    /**
     * Adds vehicle back to available list when removed from containers
     */
    protected addVehicleToAvailableList(vehicleType: string): void {
        // Find the service from API data (only facility type services)
        const service = this.apiServicesData.find(s => s.name === vehicleType && serviceTypeMatches(s, 'facility'));
        if (service) {
            const vehicle = new User(service.name, service.icon_url);
            this.availableFacilities.update(facilities => {
                const updatedFacilities = [...facilities, vehicle];
                return updatedFacilities;
            });
        }
        // Also add back to faclitiesTypes for consistency (only if it's a facility type)
        if (service && !this.faclitiesTypes.find(card => card.name === vehicleType)) {
            this.faclitiesTypes.push({ name: vehicleType });
        }
    }

    /**
     * Handles trash button click with confirmation dialog
     */
    protected onTrashButtonClick(containerId: number): void {
        const containerToDelete = this.vehicleContainers().find(container => container.id === containerId);

        if (!containerToDelete) {
            return;
        }

        const data: TuiConfirmData = {
            content: `Are you sure you want to delete the "${containerToDelete.facilityType}" facility type? This action cannot be undone.`,
            yes: 'Yes, Delete',
            no: 'No, Keep it',
        };

        this.dialogs
            .open<boolean>(TUI_CONFIRM, {
                label: 'Confirm Deletion',
                size: 's',
                data,
            })
            .pipe(
                switchMap((response) => {
                    if (response) {
                        // Delete from API if facilityTypeId exists
                        if (containerToDelete.facilityTypeId && containerToDelete.facilityTypeId !== `facility-${containerId}`) {
                            // Remove container locally immediately
                            this.vehicleContainers.update(containers =>
                                containers.filter(container => container.id !== containerId)
                            );
                            // Add facility type back to available list
                            this.faclitiesTypes.push({ name: containerToDelete.facilityType });
                            this.addVehicleToAvailableList(containerToDelete.facilityType);
                            // Then delete from API
                            return this.apiService.deleteService(containerToDelete.facilityTypeId);
                        } else {
                            // Fallback to local deletion
                            this.deleteVehicleContainerWithAPI(containerToDelete);
                            return of(null);
                        }
                    } else {
                        this.notificationService.showInfo('Deletion cancelled', 3000);
                        return of(null);
                    }
                })
            )
            .subscribe({
                next: (alertResponse) => {
                    // Show success notification after deletion
                    if (alertResponse) {
                        this.notificationService.showSuccess('Facility type deleted successfully!', 3000);
                    }
                },
                error: (error) => {
                    // Handle error if needed
                    this.notificationService.showError('Failed to delete facility type. Please try again.', 4000);
                }
            });
    }

    /**
     * Deletes facility container with API call
     */
    protected deleteVehicleContainerWithAPI(containerToDelete: any): void {
        // Set loading state
        this.deletingRowId.set(containerToDelete.id);
        this.cdr.detectChanges();

        // For now, just delete from local state
        // You can add API call here if needed

        // Add the facility type back to available faclitiesTypes
        this.faclitiesTypes.push({ name: containerToDelete.facilityType });
        // Also add back to availableVehicles for consistency
        this.addVehicleToAvailableList(containerToDelete.facilityType);

        // Remove from containers
        this.vehicleContainers.update(containers =>
            containers.filter(container => container.id !== containerToDelete.id)
        );

        // Clear loading state
        this.deletingRowId.set(null);
        this.cdr.detectChanges();
    }

    /**
     * Deletes a facility container and adds facility type back to available list
     */
    protected deleteVehicleContainer(containerId: number): void {
        const containerToDelete = this.vehicleContainers().find(container => container.id === containerId);
        if (containerToDelete) {
            // Add the facility type back to available faclitiesTypes
            this.faclitiesTypes.push({ name: containerToDelete.facilityType });
            // Also add back to availableVehicles for consistency
            this.addVehicleToAvailableList(containerToDelete.facilityType);
        }
        this.vehicleContainers.update(containers =>
            containers.filter(container => container.id !== containerId)
        );
    }

    /**
     * Toggles vehicle container active status
     */
    protected toggleVehicleActive(container: any): void {
        container.isActive = !container.isActive;
        // Update the signal to trigger change detection
        this.vehicleContainers.update(containers => [...containers]);
    }


    /**
     * Gets pricing tier count for a specific container
     */
    protected getPricingTierCount(container: any): number {
        return container.tableRows.length;
    }

    /**
     * Gets lowest price for a specific container
     */
    protected getLowestPrice(container: any): number {
        if (!container.tableRows.length) return 0;
        return Math.min(...container.tableRows.map((row: any) => row.price));
    }

    // Table Row Management Methods
    /**
     * Adds a new tier to a specific container
     */
    protected addTierToContainer(container: any): void {
        const newId = container.tableRows.length + 1;
        const newRow = {
            id: newId,
            duration: 'New Tier',
            minHours: '',
            maxHours: '',
            price: 0,
            taxAmount: 0, // Default tax amount for new rows
            withTax: 0, // Default to 0 for new rows
            type: 'Active',
            isEditMode: true
        };
        container.tableRows.push(newRow);
        // Update the signal to trigger change detection
        this.vehicleContainers.update(containers => [...containers]);
    }

    /**
     * Enters edit mode for a specific row in a container
     */
    protected editRowInContainer(container: any, row: any): void {
        row.isEditMode = true;

        // Hourly / Flat: min fixed at 0h only if no Flat exists yet
        if (this.hasFixedMinHours(row, container)) {
            this.applyFixedMinAndDefaultMax(row, { container });
        }

        // Only enable form if it exists (for existing rows)
        if (row.form) {
            row.form.enable();
            // Populate form controls with current row values
            row.form.patchValue({
                durationField: row.duration,
                minHoursField: row.minHours,
                maxHoursField: row.maxHours,
                priceField: row.price,
                withTaxField: row.withTax,
                typeField: row.unit
            });
        }

        // Update the signal to trigger change detection
        this.vehicleContainers.update(containers => [...containers]);
    }


    /**
     * Performs a simple save without API call (for testing)
     */
    protected performSimpleSave(container: any, row: any): void {
        // Set saving state for this specific row
        this.savingRowId.set(row.id);

        // Simulate API delay
        setTimeout(() => {
            // Clear saving state
            this.savingRowId.set(null);

            // Exit edit mode
            row.isEditMode = false;

            // Ensure row has a form for future editing
            if (!row.form) {
                row.form = new FormGroup({
                    durationField: new FormControl({ value: row.duration, disabled: true }),
                    minHoursField: new FormControl({ value: row.minHours, disabled: true }),
                    maxHoursField: new FormControl({ value: row.maxHours, disabled: true }),
                    priceField: new FormControl({ value: row.price, disabled: true }),
                    withTaxField: new FormControl({ value: row.withTax, disabled: true }),
                    typeField: new FormControl({ value: row.unit, disabled: true })
                });
            } else {
                // Disable existing form
                row.form.disable();
            }

            // Update the signal to trigger change detection
            this.vehicleContainers.update(containers => [...containers]);

            // Force change detection
            this.cdr.detectChanges();

            // Show success message
            const isNewRow = !row.rateId;
            const successMessage = isNewRow ? 'New pricing tier added successfully!' : 'Pricing tier updated successfully!';
            this.successMessage.set(successMessage);
            setTimeout(() => this.successMessage.set(''), 3000);

            // Show notification
            this.notificationService.showSuccess(successMessage, 3000);
        }, 1000);
    }


    /**
     * Checks if price input should show error styling
     */
    protected shouldShowPriceError(row: any): boolean {
        return row.validationAttempted && (!row.price || row.price <= 0);
    }

    protected shouldShowTierNameError(row: any): boolean {
        const name = String(row?.tierName || row?.duration || '').trim();
        return !!row?.validationAttempted && !name;
    }

    /**
     * Cancels edit mode for a specific row in a container
     */
    /**
     * Cancels edit mode for a specific row in a container
     */
    protected cancelEditInContainer(container: any, row: any): void {
        // If it's a new row (no rateId/ID from DB), remove it completely
        if (!row.rateId) {
            this.deleteRowInContainer(container, row.id);
            return;
        }

        row.isEditMode = false;

        // If form exists, disable it and reset values
        if (row.form) {
            row.form.disable();
            // Reset form values to original data if form controls exist
            const formValue: any = {};
            if (row.form.get('durationField')) formValue.durationField = row.duration;
            if (row.form.get('minHoursField')) formValue.minHoursField = row.minHours;
            if (row.form.get('maxHoursField')) formValue.maxHoursField = row.maxHours;
            if (row.form.get('priceField')) formValue.priceField = row.price;
            if (row.form.get('withTaxField')) formValue.withTaxField = row.withTax;
            if (row.form.get('typeField')) formValue.typeField = row.unit;
            if (row.form.get('unitTypeField')) formValue.unitTypeField = row.unitType;
            if (row.form.get('tierNameField')) formValue.tierNameField = row.tierName;
            if (row.form.get('availableField')) formValue.availableField = row.available;

            row.form.patchValue(formValue);
        }

        // Update the signal to trigger change detection
        this.vehicleContainers.update(containers => [...containers]);
    }

    /**
     * Deletes a specific row in a container
     */
    protected deleteRowInContainer(container: any, rowId: number): void {
        const rowToDelete = container.tableRows.find((row: any) => row.id === rowId);

        // If the row has a rateId (was saved to API), delete via new endpoint
        if (rowToDelete && rowToDelete.rateId) {
            // Set loading state for this specific row
            this.deletingRowId.set(rowId);

            // Make DELETE API call to delete the specific rate tier
            this.apiService.deleteServiceRate(rowToDelete.rateId, {
                parkingId: this.parkingId,
                orgId: this.apiService.getCurrentOrganizationId() || undefined,
            }).subscribe({
                next: (response) => {
                    // Clear loading state
                    this.deletingRowId.set(null);

                    // Refresh data from API to get the latest state for this container only
                    this.reloadContainerRates(container);

                    // Show success notification
                    this.notificationService.showSuccess('Facility pricing tier deleted successfully!', 3000);
                },
                error: (error) => {
                    this.errorMessage.set('Failed to delete facility rate.');
                    // Clear loading state
                    this.deletingRowId.set(null);

                    // Show error notification
                    this.notificationService.showError('Failed to delete facility pricing tier. Please try again.', 4000);
                }
            });
        } else {
            // If no rateId (new row), just remove from UI
            const currentRows = [...container.tableRows];
            container.tableRows = currentRows.filter((row: any) => row.id !== rowId);
            // Update the signal to trigger change detection
            this.vehicleContainers.update(containers => [...containers]);

            // Show success notification
            this.notificationService.showSuccess('Pricing tier removed successfully!', 3000);
        }
    }

    /**
     * Handles row action button click - shows confirm dialog only for trash icon
     */
    protected onRowActionButtonClick(container: any, row: any): void {
        if (row.isEditMode) {
            // If in edit mode (x icon), just cancel edit mode
            this.cancelEditInContainer(container, row);
        } else {
            // If not in edit mode (trash icon), show confirm dialog for row deletion
            this.showRowDeleteConfirmDialog(container, row);
        }
    }

    /**
     * Shows confirm dialog for row deletion
     */
    protected showRowDeleteConfirmDialog(container: any, row: any): void {
        const data: TuiConfirmData = {
            content: `Are you sure you want to delete this pricing tier? This action cannot be undone.`,
            yes: 'Yes, Delete',
            no: 'No, Keep it',
        };

        this.dialogs
            .open<boolean>(TUI_CONFIRM, {
                label: 'Confirm Row Deletion',
                size: 's',
                data,
            })
            .pipe(
                switchMap((response) => {
                    if (response) {
                        this.deleteRowFromContainer(container, row.id);
                        this.notificationService.showSuccess('Pricing tier deleted successfully!', 3000);
                        return of(null);
                    } else {
                        this.notificationService.showInfo('Row deletion cancelled', 3000);
                        return of(null);
                    }
                })
            )
            .subscribe({
                next: (alertResponse) => {
                    // Handle response if needed
                },
                error: (error) => {
                    // Handle error if needed
                }
            });
    }

    // Vehicle Icon and Theme Methods
    /**
     * Checks if dark mode is active
     */
    protected isDarkMode(): boolean {
        // Rely on the app's theme classes only. Do not auto-follow OS preference here,
        // otherwise icons stay white even when the app is in light mode.
        return document.body.classList.contains('dark') ||
            document.documentElement.classList.contains('tui-theme-dark');
    }

    /**
     * Gets vehicle icon SVG code
     */
    protected getVehicleIcon(vehicleType: string): string {
        return this.vehicleIconsService.getSvgCodeByNameWithTheme(vehicleType, this.isDarkMode());
    }

    /**
     * Gets vehicle icon as data URL for img tag
     */
    protected getVehicleIconAsDataUrl(vehicleType: string): string {
        const svgCode = this.vehicleIconsService.getSvgCodeByNameWithTheme(vehicleType, this.isDarkMode());
        if (svgCode) {
            const encodedSvg = encodeURIComponent(svgCode);
            return `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
        }
        return '';
    }

    /**
     * Gets vehicle icon for img tag (alias for getVehicleIconAsDataUrl)
     */
    protected getVehicleIconSrc(vehicleType: string): string {
        return this.getVehicleIconAsDataUrl(vehicleType);
    }

    /**
     * Gets vehicle background color based on vehicle type
     */
    protected getVehicleBackgroundColor(vehicleType: string): string {
        return this.vehicleIconsService.getVehicleBackgroundColorByTheme(vehicleType, this.isDarkMode());
    }

    /**
     * Gets vehicle text color for better contrast
     */
    protected getVehicleTextColor(vehicleType: string): string {
        return this.vehicleIconsService.getVehicleTextColorByTheme(vehicleType, this.isDarkMode());
    }

    // Currency and Locale Methods
    /**
     * Handles currency change event
     */
    protected onCurrencyChange(event: Event): void {
        const target = event.target as HTMLSelectElement;
        if (target && target.value) {
            this.changeCurrency(target.value);
        }
    }

    /**
     * Changes currency and updates locale
     */
    protected changeCurrency(currency: string): void {
        this.currentCurrency = currency;
        this.updateLocaleForCurrency(currency);
    }

    /**
     * Updates locale based on currency for RTL support
     */
    protected updateLocaleForCurrency(currency: string): void {
        const currencyLocaleMap: { [key: string]: string } = {
            'USD': 'en-US',
            'EUR': 'en-EU',
            'GBP': 'en-GB',
            'SAR': 'ar-SA', // Saudi Riyal - RTL
            'AED': 'ar-AE', // UAE Dirham - RTL
            'QAR': 'ar-QA', // Qatari Riyal - RTL
            'KWD': 'ar-KW', // Kuwaiti Dinar - RTL
            'BHD': 'ar-BH', // Bahraini Dinar - RTL
            'OMR': 'ar-OM', // Omani Rial - RTL
            'JOD': 'ar-JO', // Jordanian Dinar - RTL
            'LBP': 'ar-LB', // Lebanese Pound - RTL
            'EGP': 'ar-EG', // Egyptian Pound - RTL
            'ILS': 'he-IL', // Israeli Shekel - RTL
        };

        this.currentLocale = currencyLocaleMap[currency] || 'en-US';
        this.isRTL = this.currentLocale.startsWith('ar') || this.currentLocale.startsWith('he');
    }

    /**
     * Gets text direction for RTL support
     */
    protected getTextDirection(): string {
        return this.isRTL ? 'rtl' : 'ltr';
    }

    /**
     * Gets current currency symbol based on selected region
     */
    get currencySymbol(): string {
        const currentRegion = this.currencyService.getCurrentRegion();
        return currentRegion ? currentRegion.currency_symbol : 'د.إ'; // Changed default to AED
    }

    /**
     * Handle region change
     */
    protected onRegionChange(region: any): void {
        if (region) {
            this.currencyService.setCurrentRegion(region);
            this.selectedRegion = region;
        }
    }

    // Form Validation and Error Handling
    /**
     * Error message for vehicle selection
     */
    protected get vehicleSelectionError(): string | null {
        const control = this.vehicleSelectionForm.controls.selectedCard;
        if (control.errors && control.touched) {
            return 'Please select a vehicle type.';
        }
        return null;
    }

    // Utility Methods
    /**
     * Tracks items by index for ngFor optimization
     */
    protected trackByIndex(index: number): number {
        return index;
    }

    /**
     * Calculates total for an item
     */
    protected getTotal(item: any): number {
        return item.price * item.quantity;
    }


    /**
     * Handles value changes in form fields
     */
    protected onValueChange(value: any, field: string, item: any, array: any[]): void {
        // mark row as dirty so only changed rows are submitted
        (item as any)._dirty = true;
        if (field === 'minHours' || field === 'maxHours') {
            // Min fixed at 0h only when no Flat exists in the list yet
            if (field === 'minHours' && this.hasFixedMinHours(item, { tableRows: array })) {
                item.minHours = '0h';
                return;
            }
            // Store the numeric value directly (without 'h')
            const next = value !== null && value !== undefined && value !== '' ? value.toString() : '';
            item[field] = next;
        } else if (field === 'unitType') {
            item[field] = value;
            // Defaults: min 0h (if locked); max default by unit type (editable)
            this.applyFixedMinAndDefaultMax(item, {
                forceMaxDefault: true,
                container: { tableRows: array }
            });
        } else if (field === 'unit') {
            item[field] = value;
            // Also update the type field to match unit for consistency
            item.type = value;
        } else if (field === 'tierName') {
            item.tierName = value;
            item.duration = value;
        } else if (field === 'duration') {
            item.duration = value;
            item.tierName = value;
        } else {
            item[field] = value;
        }
        // Recalculate withTax if price changes
        if (field === 'price') {
            const price = parseFloat(value) || 0;
            const taxAmount = item.taxAmount || 0; // Use original tax amount from API
            item.withTax = price + taxAmount; // With Tax = price + tax amount
        }
        if (field === 'price' || field === 'quantity') {
            item.total = this.getTotal(item);
        }
    }

    /** True when row unit type is Hourly (Time Based hourly slab). */
    protected isHourlyUnitType(row: any): boolean {
        return String(row?.unitType || '').toLowerCase().trim() === 'hourly';
    }

    /** True when row unit type is Flat / Flat Rate. */
    protected isFlatUnitType(row: any): boolean {
        const type = String(row?.unitType || '').toLowerCase().trim();
        return type === 'flat' || type === 'flat rate';
    }

    /** True when container already has at least one Flat unit-type item. */
    protected containerHasFlatUnit(container: any): boolean {
        const rows = container?.tableRows;
        if (!Array.isArray(rows)) return false;
        return rows.some((row: any) => this.isFlatUnitType(row));
    }

    /**
     * Min hours locked at 0h for Hourly/Flat — until a Flat item already exists.
     * If list already has Flat, new/other items get editable min hours.
     */
    protected hasFixedMinHours(row: any, container?: any): boolean {
        if (!this.isHourlyUnitType(row) && !this.isFlatUnitType(row)) {
            return false;
        }
        if (container && this.containerHasFlatUnit(container)) {
            return false;
        }
        return true;
    }

    protected getMaxHoursInputMin(row: any): number {
        return 0;
    }

    protected getMaxHoursInputMax(_row: any): number {
        return Infinity;
    }

    /**
     * Min 0h when locked. Flat defaults to 24h. Max hours stays editable.
     */
    protected applyFixedMinAndDefaultMax(
        row: any,
        options?: { forceMaxDefault?: boolean; container?: any }
    ): void {
        const lockMin = this.hasFixedMinHours(row, options?.container);
        if (lockMin) {
            row.minHours = '0h';
        }
        const forceMax = options?.forceMaxDefault === true;
        const maxEmpty = !row.maxHours && row.maxHours !== 0;
        if (this.isFlatUnitType(row) && (forceMax || maxEmpty)) {
            row.maxHours = '24h';
        }
        if (row.form) {
            row.form.patchValue(
                { minHoursField: row.minHours, maxHoursField: row.maxHours },
                { emitEvent: false }
            );
        }
    }

    /**
     * Adds a new row to a container with smart duration calculation
     */
    protected addNewRowToContainer(container: any): void {
        // Directly add new tier without confirm dialog
        this.performAddNewRowToContainer(container);
    }

    /**
     * Shows confirm dialog for adding new tier
     */
    protected showAddTierConfirmDialog(container: any): void {
        const data: TuiConfirmData = {
            content: `Are you sure you want to add a new pricing tier to "${container.facilityType}"? This will create a new pricing configuration.`,
            yes: 'Add Tier',
            no: 'Cancel',
        };

        this.dialogs
            .open<boolean>(TUI_CONFIRM, {
                label: 'Confirm Add Tier',
                size: 's',
                data,
            })
            .pipe(
                switchMap((response) => {
                    if (response) {
                        this.performAddNewRowToContainer(container);
                        this.notificationService.showSuccess('New tier added! Please fill in the details and save.', 3000);
                        return of(null);
                    } else {
                        this.notificationService.showInfo('Add tier cancelled', 3000);
                        return of(null);
                    }
                })
            )
            .subscribe({
                next: (alertResponse) => {
                    // Handle response if needed
                },
                error: (error) => {
                    // Handle error if needed
                }
            });
    }

    /**
     * Performs the actual add new row operation after user confirmation
     */
    protected performAddNewRowToContainer(container: any): void {
        const rows = container.tableRows;
        // Determine default unit type based on pricing type
        const isValet = container.facilityType && container.facilityType.toLowerCase().includes('valet');
        const defaultUnitType = (container.pricingType === 'Flat Rate' && !isValet) ? 'Flat Rate' : 'Hourly';
        // If a Flat item already exists, min hours stays editable for the new row
        const hasFlatAlready = this.containerHasFlatUnit(container);
        const lockMin = !hasFlatAlready && this.hasFixedMinHours({ unitType: defaultUnitType }, container);
        const defaultMinHours = lockMin
            ? '0h'
            : (hasFlatAlready ? '' : (this.hasFixedMinHours({ unitType: defaultUnitType }) ? '0h' : ''));
        const defaultMaxHours = this.isFlatUnitType({ unitType: defaultUnitType })
            ? '24h'
            : (this.isHourlyUnitType({ unitType: defaultUnitType }) ? '1h' : '');

        rows.push({
            id: rows.length === 0 ? 1 : Math.max(...rows.map((row: any) => row.id), 0) + 1,
            unitType: defaultUnitType,
            duration: '',
            minHours: defaultMinHours,
            maxHours: defaultMaxHours,
            price: null,
            taxAmount: 0,
            taxPercent: 0,
            withTax: 0,
            type: '',
            unit: '',
            description: '',
            isEditMode: true,
            form: new FormGroup({
                unitTypeField: new FormControl({ value: defaultUnitType, disabled: false }),
                durationField: new FormControl({ value: '', disabled: false }),
                minHoursField: new FormControl({ value: defaultMinHours, disabled: false }),
                maxHoursField: new FormControl({ value: defaultMaxHours, disabled: false }),
                priceField: new FormControl({ value: null, disabled: false }),
                withTaxField: new FormControl({ value: 0, disabled: true }),
                typeField: new FormControl({ value: '', disabled: false })
            })
        });

        // Show success message for new tier creation
        this.successMessage.set('New pricing tier created! Please fill in the details and save.');
        setTimeout(() => this.successMessage.set(''), 3000);

        // Update the signal to trigger change detection
        this.vehicleContainers.update(containers => [...containers]);
    }

    /**
     * Checks if a container has a 24h tier (maxHours = 24h)
     */
    protected has24hTier(container: any): boolean {
        return container.tableRows.some((row: any) => {
            const maxHours = this.getNumericValue(row.maxHours);
            return maxHours === 24;
        });
    }

    /**
     * Checks if a container has a 24h tier (maxHours = 24h) - public method for template
     */
    public has24hTierPublic(container: any): boolean {
        return this.has24hTier(container);
    }

    /**
     * Formats hours value by adding 'h' if not present
     */
    protected formatHoursValue(value: string): string {
        if (!value) return '';

        // Remove any existing 'h' and trim whitespace
        const cleanValue = value.replace(/h/gi, '').trim();

        // If it's a valid number, add 'h'
        if (cleanValue && !isNaN(Number(cleanValue))) {
            return `${cleanValue}h`;
        }

        return value; // Return original value if not a valid number
    }

    /**
     * Gets numeric value from hours string (for input display)
     */
    protected getNumericValue(hoursString: string): number {
        if (!hoursString) return 0;

        // Remove 'h' and convert to number
        const numericValue = hoursString.replace(/h/gi, '').trim();
        return isNaN(Number(numericValue)) ? 0 : Number(numericValue);
    }

    /**
     * For numeric inputs: return null when empty so placeholders show instead of 0
     */
    protected getNumericValueForInput(hoursString: string): number | null {
        if (!hoursString) return null;
        const numericValue = hoursString.replace(/h/gi, '').trim();
        if (numericValue === '') return null;
        return isNaN(Number(numericValue)) ? null : Number(numericValue);
    }

    /**
     * Formats facility name from API key to display name
     */
    protected formatFacilityName(facilityKey: string): string {
        if (!facilityKey) return '';
        switch (facilityKey.toLowerCase()) {
            case 'helmet':
            case 'helmet locker':
            case 'helmet_locker':
                return 'Helmet Locker';
            case 'storage':
            case 'storage locker':
            case 'storage_locker':
            case 'storagelocker':
            case 'locker storage':
            case 'locker_storage':
            case 'lockerstorage':
                return 'Locker Storage';
            case 'refreshment':
            case 'refreshments':
                return 'Refreshments';
            case 'valet':
            case 'valet parking':
            case 'valet_parking':
            case 'valet parking service':
                return 'Valet Parking Service';
            default:
                return facilityKey
                    .replace(/[-_]/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());
        }
    }

    /**
     * Gets facility description based on facility type and time range
     */
    protected getFacilityDescription(facilityKey: string, startHour: number, endHour: number): string {
        return '';
    }

    /**
     * Calculates duration based on min and max hours
     */
    protected calculateDuration(minHours: string, maxHours: string): string {
        if (!minHours && !maxHours) {
            return 'Enter duration';
        }

        if (minHours && maxHours) {
            return `${minHours} - ${maxHours}`;
        }

        if (minHours) {
            return `${minHours}+`;
        }

        if (maxHours) {
            return `0 - ${maxHours}`;
        }

        return 'Enter duration';
    }

    /**
     * Calculate price based on unit type and duration
     */
    protected calculatePriceByUnitType(unitType: string, basePrice: number, minHours: number, maxHours: number): number {
        if (unitType === 'Hourly') {
            // For hourly, price is per hour
            const duration = maxHours - minHours;
            return basePrice * duration;
        } else if (unitType === 'Flat Rate') {
            // For flat rate, price is fixed regardless of duration
            return basePrice;
        }
        return basePrice;
    }

    /**
     * Get unit type display text
     */
    protected getUnitTypeDisplayText(unitType: string): string {
        switch (unitType) {
            case 'Hourly':
                return 'Per Hour';
            case 'Flat Rate':
                return 'Fixed Rate';
            default:
                return unitType;
        }
    }

    // Public Methods for External Access
    /**
     * Adds vehicle type from button click with validation
     */
    public addVehicleTypeFromButton(): void {
        // Mark the form control as touched to trigger validation
        const control = this.vehicleSelectionForm.controls.selectedCard;
        control.markAsTouched();

        if (control.value && this.vehicleSelectionForm.valid) {
            // Create container using existing service data structure
            this.createContainerFromExistingService(control.value);
            this.selectedCard = null; // Reset selection after creating container
            this.selectedTypeValue.set(null);
            this.vehicleSelectionForm.reset(); // Reset form
            setTimeout(() => {
                this.cdr.detectChanges(); // Trigger change detection after form reset
            });
        }
    }

    /**
     * Opens facility drawer with selected facility data prefilled
     */
    public openFacilityDrawerWithSelection(): void {
        if (this.isAddTypeDisabled()) {
            return;
        }

        // Get the selected facility from the dropdown
        const selectedFacility = this.vehicleSelectionForm.controls.selectedCard.value;

        // Mark field as touched to show validation errors
        this.vehicleSelectionForm.controls.selectedCard.markAsTouched();

        // Validate if facility is selected
        if (!selectedFacility) {
            // this.notificationService.showError('Please select a facility type from the dropdown before adding.', 3000);
            return;
        }

        if (this.facilityDrawer) {
            if (selectedFacility) {
                // Map unit type from existing service to drawer defaults
                const unitTypeObj = (selectedFacility as any)?.unitType;
                const existingUnitLabel = unitTypeObj?.label || '';
                const existingUnitCode = unitTypeObj?.code || '';

                // Pricing type shown as label (e.g., Hour Based, Flat Rate, Time Based, Energy Consumption)
                const pricingType = existingUnitLabel || this.getPricingTypeFromUnitTypeId(((selectedFacility as any)?.unit_type_id || ''));

                // Drawer secondary unitType control expects 'hourly' | 'flat'
                let unitTypeValue = 'hourly';
                const codeLower = String(existingUnitCode).toLowerCase();
                if (codeLower === 'fixed') unitTypeValue = 'flat';
                else if (codeLower === 'hourly') unitTypeValue = 'hourly';
                // For time_based/energy/day/unit keep sensible default 'hourly'

                // Prefill data for the drawer
                // Force Per Unit for specific facility types regardless of API value
                const facilityNameLower = (selectedFacility.name || '').toLowerCase();
                let finalPricingType = pricingType;
                if (facilityNameLower.includes('refreshment') || facilityNameLower.includes('restroom')) {
                    finalPricingType = 'Per Unit';
                } else if (facilityNameLower.includes('valet')) {
                    const current = String(finalPricingType || '').toLowerCase().trim();
                    if (current === 'flat rate' || current === 'flat') {
                        finalPricingType = 'Per Unit';
                    }
                } else if (facilityNameLower.includes('locker')) {
                    const current = String(finalPricingType || '').toLowerCase().trim();
                    finalPricingType = current === 'per unit' ? 'Per Unit' : 'Time Based';
                }

                const prefilledData = {
                    facilityName: selectedFacility.name,
                    description: '',
                    pricingType: finalPricingType,
                    unitType: unitTypeValue,
                    location: '',
                    capacity: '',
                    tax: 'GST',
                    uploadedImages: [],
                    service_id: (selectedFacility as any).id || null, // Pass the service ID
                    is_session_based: (selectedFacility as any).is_session_based === true
                        || (selectedFacility as any).is_session_based === 'true'
                        || (selectedFacility as any).is_session_based === 1
                        || (selectedFacility as any).isSessionBased === true
                };

                // Open drawer with prefilled data
                this.facilityDrawer.openForCreateWithData(prefilledData);
            } else {
                // No selection, open drawer normally
                this.facilityDrawer.openForCreate();
            }
        }
    }

    /**
     * Deletes a row from a container
     */
    public deleteRowFromContainer(container: any, rowId: number): void {
        if (typeof this.deleteRowInContainer === 'function') {
            this.deleteRowInContainer(container, rowId);
        } else {
            container.tableRows = container.tableRows.filter((row: any) => row.id !== rowId);
            // Update the signal to trigger change detection
            this.vehicleContainers.update(containers => [...containers]);
        }
    }

    /**
     * Adds facility type from selection
     */
    public addVehicleTypeFromSelection(facilityType: string): void {
        const newContainerId = Math.max(...this.vehicleContainers().map(c => c.id), 0) + 1;

        // Find the service from API data (only facility type services)
        const apiService = this.apiServicesData.find(s => s.name === facilityType && serviceTypeMatches(s, 'facility'));

        // Find the facility type from faclitiesTypes to get the unit type ID
        const facilityTypeData = this.faclitiesTypes.find(ft => ft.name === facilityType);
        const unitTypeId = facilityTypeData?.unit_type_id;

        // Also check if we have facility unit type mapping from rate slots data
        let mappedUnitTypeId = unitTypeId;
        if (!mappedUnitTypeId && this.rateSlotsData?.facilityUnitTypeMap) {
            mappedUnitTypeId = this.rateSlotsData.facilityUnitTypeMap.get(facilityType);
        }

        // Determine pricing type based on unit type ID if available, otherwise fallback to service type
        let pricingType = null;
        if (mappedUnitTypeId) {
            pricingType = this.getPricingTypeFromUnitTypeId(mappedUnitTypeId);
        } else if (apiService) {
            pricingType = this.getPricingTypeFromServiceType(apiService.service_type);
        } else {
            // Fallback to a default pricing type to ensure container is created
            pricingType = 'Time Based';
        }

        const newContainer = {
            id: newContainerId,
            facilityType: facilityType,
            facilityTypeId: apiService ? apiService.id : `facility-${newContainerId}`,
            priceId: null,
            isActive: true,
            pricingType: pricingType,
            description: apiService ? apiService.description : '',
            uploadedImages: [],
            tableRows: [],
            service_taxes: [] // Will be populated when rates are loaded
        };

        this.vehicleContainers.update(containers => [...containers, newContainer]);
        // Remove the selected facility type from available faclitiesTypes
        this.faclitiesTypes = this.faclitiesTypes.filter(card => card.name !== facilityType);
        // Also remove from availableVehicles for consistency

        // Load rates for the new container
        setTimeout(() => {
            // this.loadAllServiceRatesForContainers();
        }, 100);
        this.removeFacilityFromAvailableList(facilityType);
    }

    /**
     * Creates container from existing service data using exisitng_service_id
     */
    public createContainerFromExistingService(selectedFacility: any): void {
        const newContainerId = Math.max(...this.vehicleContainers().map(c => c.id), 0) + 1;

        // Create service data structure similar to the provided JSON
        const serviceData = {
            parking_id: "bad09454-a339-4790-a5fc-90a56abac72a",
            org_id: "944d0be1-b97c-4404-9a7f-9472649ac1c8",
            exisitng_service_id: "298a98a1-d8c1-421e-990e-0cb133f7dd55", // Using the provided existing service ID
            service_type: "facility",
            capacity: 1112,
            status: true,
            org_tax_ids: [
                "a998748f-24d9-4dcd-bccb-2b11de5940d2",
                "a2ac4c4b-c200-4525-b84d-d002a97b1fb5"
            ],
            unit_type_id: "a0b90dd5-e8b8-412a-902b-517b34ffd13f"
        };

        // Find the facility type from faclitiesTypes to get additional data
        const facilityTypeData = this.faclitiesTypes.find(ft => ft.name === selectedFacility.name);

        // Determine pricing type based on unit type ID
        let pricingType = this.getPricingTypeFromUnitTypeId(serviceData.unit_type_id);
        if (!pricingType) {
            pricingType = 'Time Based'; // Fallback pricing type
        }

        const newContainer = {
            id: newContainerId,
            facilityType: selectedFacility.name,
            facilityTypeId: serviceData.exisitng_service_id, // Use the existing service ID
            priceId: null,
            isActive: serviceData.status,
            pricingType: pricingType,
            description: '', // Use default description since FacilityType doesn't have description
            capacity: serviceData.capacity,
            uploadedImages: [],
            tableRows: [],
            serviceData: serviceData, // Store the complete service data for reference
            service_taxes: [] // Will be populated when rates are loaded
        };

        this.vehicleContainers.update(containers => [...containers, newContainer]);

        // Remove the selected facility type from available faclitiesTypes
        this.faclitiesTypes = this.faclitiesTypes.filter(card => card.name !== selectedFacility.name);
        // Also remove from availableVehicles for consistency
        this.removeFacilityFromAvailableList(selectedFacility.name);


        // Load rates for the new container
        setTimeout(() => {
            // this.loadAllServiceRatesForContainers();
        }, 100);
    }

    /**
     * Test method to verify confirm dialog is working
     */
    protected testConfirmDialog(): void {
        const data: TuiConfirmData = {
            content: 'This is a test confirmation dialog. Do you like it?',
            yes: 'Yes, I like it!',
            no: 'No, I don\'t like it',
        };

        this.dialogs
            .open<boolean>(TUI_CONFIRM, {
                label: 'Test Confirmation',
                size: 's',
                data,
            })
            .pipe(
                switchMap((response) => {
                    if (response) {
                        this.notificationService.showSuccess('Great! You liked it!', 3000);
                        return of(null);
                    } else {
                        this.notificationService.showInfo('Oh no! You didn\'t like it!', 3000);
                        return of(null);
                    }
                })
            )
            .subscribe({
                next: (alertResponse) => {
                    // Handle response if needed
                },
                error: (error) => {
                    // Handle error if needed
                }
            });
    }

    /**
     * Test method to verify save confirm dialog is working
     */
    protected testSaveConfirmDialog(): void {
        // Create a mock row for testing
        const mockRow = {
            id: 999,
            rateId: null, // This makes it a new row
            price: 10,
            minHours: '0h',
            maxHours: '1h',
            duration: '0h - 1h',
            withTax: 10,
            unit: 'Active',
            isEditMode: true
        };

        const mockContainer = {
            id: 999,
            facilityType: 'Test Facility',
            priceId: 'test-price-id',
            isActive: true,
            tableRows: [mockRow]
        };

        this.showSaveConfirmDialog(mockContainer, mockRow);
    }

    /**
     * Test method to verify notifications are working
     */
    protected testNotifications(): void {
        // Test success notification
        this.notificationService.showSuccess('Success notification test!', 3000);

        // Test error notification after 1 second
        setTimeout(() => {
            this.notificationService.showError('Error notification test!', 3000);
        }, 1000);

        // Test info notification after 2 seconds
        setTimeout(() => {
            this.notificationService.showInfo('Info notification test!', 3000);
        }, 2000);

        // Test warning notification after 3 seconds
        setTimeout(() => {
            this.notificationService.showWarning('Warning notification test!', 3000);
        }, 3000);
    }

    // Handle facility creation/update from drawer
    private facilityRefreshTimer: ReturnType<typeof setTimeout> | null = null;

    protected onFacilityCreated(facilityData: any): void {
        // Drop the edit payload so a reload cannot reopen the drawer.
        this.facilityDataForEdit = null;

        if (facilityData.isEdit) {
            // Update existing facility
            this.updateExistingFacility(facilityData);
        } else {
            // Create new facility
            this.createNewFacility(facilityData);

            // Auto-assign new facility to all existing parking users
            const newFacilityId = facilityData.serviceId;
            if (newFacilityId && this.parkingId) {
                this.autoAssignNewFacilityToAllUsers(newFacilityId, this.parkingId);
            }
        }

        // Reset the selection dropdown
        this.selectedCard = null;
        this.selectedTypeValue.set(null);
        this.vehicleSelectionForm.reset();
        this.vehicleSelectionForm.controls.selectedCard.setValue(null);

        // One refresh for stacked create/update events. A second emit used to
        // turn loading back on and leave the drawer stuck on "Updated".
        if (this.facilityRefreshTimer) {
            clearTimeout(this.facilityRefreshTimer);
        }
        this.isLoading.set(true);
        this.loadFacilityRateSlots();
        this.loadServices();
        this.loadFacilityTypesFromParkingDetail(true);

        this.facilityRefreshTimer = setTimeout(() => {
            this.facilityRefreshTimer = null;
            this.loadAllServiceRatesForContainers();
            this.isLoading.set(false);
            this.cdr.detectChanges();
        }, 1000);
    }

    /**
     * Jab naya facility parking me add hota hai, tab us parking ke
     * sabhi existing users ko wo naya facility automatically assign karta hai.
     * Ye silently background me chalta hai — user ko koi interruption nahi hoti.
     */
    private autoAssignNewFacilityToAllUsers(newFacilityId: string, parkingId: string): void {
        console.log('[AutoAssign] New facility created:', newFacilityId, 'for parking:', parkingId);

        this.apiService.getParkingUsers(parkingId).pipe(
            catchError(err => {
                console.warn('[AutoAssign] Could not fetch parking users:', err);
                return of([]);
            }),
            map((response: any) => {
                if (Array.isArray(response)) return response;
                if (response?.data && Array.isArray(response.data)) return response.data;
                if (response?.users && Array.isArray(response.users)) return response.users;
                if (response?.data?.users && Array.isArray(response.data.users)) return response.data.users;
                if (response?.rows && Array.isArray(response.rows)) return response.rows;
                if (response?.data?.rows && Array.isArray(response.data.rows)) return response.data.rows;
                return [];
            }),
            switchMap((users: any[]) => {
                if (!users || users.length === 0) return of([]);

                const requests = users
                    .filter((user: any) => !!(user?.id || user?.user_id))
                    .map((user: any) => {
                        const userId = user.id || user.user_id;
                        return this.apiService.getUserById(userId).pipe(
                            map((fullUser: any) => ({ ...fullUser, originalId: userId })),
                            catchError(() => of(null))
                        );
                    });
                return forkJoin(requests);
            })
        ).subscribe({
            next: (fullUsers: any[]) => {
                const validUsers = fullUsers.filter(u => u !== null);
                if (validUsers.length === 0) return;

                const updateRequests = validUsers.map((user: any) => {
                    let serviceAccess = user.service_access;
                    if (typeof serviceAccess === 'string') {
                        try { serviceAccess = JSON.parse(serviceAccess); } catch { serviceAccess = null; }
                    }
                    if (serviceAccess && !Array.isArray(serviceAccess)) serviceAccess = [serviceAccess];

                    let existingEntry = (Array.isArray(serviceAccess) && serviceAccess.length > 0)
                        ? serviceAccess[0]
                        : { vehicle: false, passes: false, facility: [] };

                    let facilityList: string[] = [];
                    if (existingEntry.facility === true || existingEntry.facility === 'all') {
                        facilityList = ['all'];
                    } else if (Array.isArray(existingEntry.facility)) {
                        facilityList = [...existingEntry.facility];
                    }

                    if (facilityList.includes('all') || facilityList.includes(newFacilityId)) return of(null);

                    facilityList.push(newFacilityId);
                    const updatedServiceAccess = [{
                        ...existingEntry,
                        facility: facilityList
                    }];

                    return this.apiService.updateUser({
                        id: user.originalId,
                        service_access: updatedServiceAccess
                    }).pipe(catchError(() => of(null)));
                });

                forkJoin(updateRequests).subscribe();
            }
        });
    }

    /**
     * Create new facility
     */
    protected createNewFacility(facilityData: any): void {
        // Create a new container for the facility
        const newContainerId = this.vehicleContainers().length + 1;

        // Find the service from API data (only facility type services)
        const apiService = this.apiServicesData.find(s => s.name === facilityData.facilityName && serviceTypeMatches(s, 'facility'));

        // Find the facility type from faclitiesTypes to get the unit type ID
        const facilityTypeData = this.faclitiesTypes.find(ft => ft.name === facilityData.facilityName);
        const unitTypeId = facilityTypeData?.unit_type_id;

        // Determine pricing type based on unit type ID if available, otherwise fallback to service type
        let pricingType = null;
        if (unitTypeId) {
            pricingType = this.getPricingTypeFromUnitTypeId(unitTypeId);
        } else if (facilityData.pricingType) {
            pricingType = facilityData.pricingType;
        } else if (apiService) {
            pricingType = this.getPricingTypeFromServiceType(apiService.service_type);
        } else {
            // Fallback to a default pricing type to ensure container is created
            pricingType = 'Time Based';
        }

        // Create service_taxes array from selectedTaxes with complete information
        const serviceTaxes = (facilityData.selectedTaxes || []).map((tax: any) => {
            const isPercentage = tax.mode === 'percent' || tax.mode === 'percentage';
            return {
                taxes: {
                    id: tax.id,
                    name: tax.name,
                    rate_percent: isPercentage ? tax.value : null,
                    rate_amount: !isPercentage ? tax.value : null,
                    type: isPercentage ? 'percentage' : 'fixed'
                }
            };
        });

        const newContainer = {
            id: newContainerId,
            facilityType: facilityData.facilityName,
            facilityTypeId: apiService ? apiService.id : `facility-${newContainerId}`,
            priceId: null,
            isActive: true,
            is_session_based: facilityData.is_session_based || false,
            pricingType: pricingType,
            description: apiService ? apiService.description : (facilityData.description || ''),
            capacity: apiService && apiService.capacity !== undefined ? apiService.capacity.toString() : (facilityData.capacity || '0'),
            uploadedImages: facilityData.uploadedImages || [], // Store uploaded images
            tableRows: this.createSampleRowsForPricingType(pricingType),
            service_taxes: serviceTaxes, // Include complete tax information
            selectedTaxIds: facilityData.org_tax_ids || [],
            created_at: (apiService as any)?.created_at || (apiService as any)?.createdAt || new Date().toISOString(),
            createdAt: (apiService as any)?.createdAt || (apiService as any)?.created_at || new Date().toISOString(),
            serviceData: apiService
        };

        // Add the new container to the list
        const updatedContainers = this.sortContainersByCreatedAt([...this.vehicleContainers(), newContainer]);
        this.vehicleContainers.set(updatedContainers);

        // Remove the facility type from available list
        this.removeFacilityFromAvailableList(facilityData.facilityName);
    }

    /**
     * Update existing facility
     */
    protected updateExistingFacility(facilityData: any): void {
        // Create service_taxes array from selectedTaxes with complete information
        const serviceTaxes = (facilityData.selectedTaxes || []).map((tax: any) => {
            const isPercentage = tax.mode === 'percent' || tax.mode === 'percentage';
            return {
                taxes: {
                    id: tax.id,
                    name: tax.name,
                    rate_percent: isPercentage ? tax.value : null,
                    rate_amount: !isPercentage ? tax.value : null,
                    type: isPercentage ? 'percentage' : 'fixed'
                }
            };
        });

        // Find the existing container to update
        this.vehicleContainers.update(containers =>
            containers.map(container => {
                if (container.facilityType === facilityData.originalData.facilityName) {
                    // Update the container with new data
                    return {
                        ...container,
                        facilityType: facilityData.facilityName,
                        pricingType: facilityData.pricingType,
                        is_session_based: facilityData.is_session_based || false,
                        description: facilityData.description || container.description || '',
                        capacity: facilityData.capacity || container.capacity || '0', // Update capacity
                        uploadedImages: Array.isArray(facilityData.uploadedImages) ? facilityData.uploadedImages : container.uploadedImages,
                        service_taxes: serviceTaxes, // Include complete tax information
                        selectedTaxIds: facilityData.org_tax_ids || [],
                        // Update table rows if pricing type changed
                        tableRows: facilityData.pricingType !== facilityData.originalData.pricingType
                            ? this.createSampleRowsForPricingType(facilityData.pricingType)
                            : container.tableRows
                    };
                }
                return container;
            })
        );
    }

    /**
     * Remove an image from the facility's uploaded images
     */
    protected removeImage(container: any, imageIndex: number): void {
        if (container.uploadedImages && container.uploadedImages.length > imageIndex) {
            container.uploadedImages.splice(imageIndex, 1);
            // Update the signal to trigger change detection
            this.vehicleContainers.update(containers => [...containers]);
        }
    }

    /**
     * Show image preview
     */
    protected showImagePreview(container: any, imageIndex: number): void {
        this.previewContainer = container;
        this.previewIndex = imageIndex;

        this.previewService.open(this.preview || '').subscribe({
            complete: () => { },
        });
    }

    /**
     * Show static image preview for fallback images
     */
    protected showStaticImagePreview(container: any, imageIndex: number): void {
        this.previewContainer = container;
        this.previewIndex = imageIndex;

        this.previewService.open(this.preview || '').subscribe({
            complete: () => { },
        });
    }

    /**
     * Get preview content for current image
     */
    protected get previewContent(): PolymorpheusContent {
        if (!this.previewContainer) {
            return '';
        }

        // If container has uploaded images, use them
        if (this.previewContainer.uploadedImages && this.previewContainer.uploadedImages.length > 0) {
            return this.getImageUrl(this.previewContainer.uploadedImages[this.previewIndex]);
        }

        // For static images, return the appropriate static image URL
        const staticImages: string[] = [];

        if (this.previewIndex >= 0 && this.previewIndex < staticImages.length) {
            return staticImages[this.previewIndex];
        }

        return '';
    }

    /**
     * Get preview title for current image
     */
    protected get previewTitle(): string {
        if (!this.previewContainer) {
            return '';
        }
        return `${this.previewContainer.facilityType} - Image ${this.previewIndex + 1}`;
    }

    /**
     * Get preview length (total images)
     */
    protected get previewLength(): number {
        if (!this.previewContainer) {
            return 0;
        }

        // If container has uploaded images, use their length
        if (this.previewContainer.uploadedImages && this.previewContainer.uploadedImages.length > 0) {
            return this.previewContainer.uploadedImages.length;
        }

        // For static images, return the number of static images
        return 0; // No static images
    }

    /**
     * Handle preview swipe
     */
    protected onPreviewSwipe(swipe: TuiSwipeEvent): void {
        if (!this.previewContainer) {
            return;
        }

        if (swipe.direction === 'left') {
            this.previewIndex = tuiClamp(this.previewIndex + 1, 0, this.previewLength - 1);
        }

        if (swipe.direction === 'right') {
            this.previewIndex = tuiClamp(this.previewIndex - 1, 0, this.previewLength - 1);
        }
    }

    /**
     * Download current image
     */
    protected downloadImage(): void {
        if (!this.previewContainer) {
            return;
        }

        let imageUrl: string;
        let fileName: string;

        // If container has uploaded images, use them
        if (this.previewContainer.uploadedImages && this.previewContainer.uploadedImages.length > 0) {
            imageUrl = this.getImageUrl(this.previewContainer.uploadedImages[this.previewIndex]);
            fileName = `${this.previewContainer.facilityType}_image_${this.previewIndex + 1}.jpg`;
        } else {
            // For static images
            const staticImages: string[] = [];
            imageUrl = staticImages[this.previewIndex] || '';
            fileName = `${this.previewContainer.facilityType}_static_${this.previewIndex + 1}.svg`;
        }

        if (imageUrl) {
            const link = document.createElement('a');
            link.href = imageUrl;
            link.download = fileName;
            link.click();

            this.notificationService.showInfo('Downloading...', 2000);
        }
    }

    /**
 * Delete current image from preview
 */
    protected deleteImageFromPreview(): void {
        if (!this.previewContainer) {
            return;
        }

        // Only allow deletion of uploaded images, not static images
        if (this.previewContainer.uploadedImages && this.previewContainer.uploadedImages.length > 0) {
            this.removeImage(this.previewContainer, this.previewIndex);
            this.notificationService.showInfo('Deleting...', 2000);
        } else {
            // For static images, show a message that they can't be deleted
            this.notificationService.showInfo('Static images cannot be deleted', 3000);
        }
    }

    /**
     * Handle edit button click - open drawer with facility data from API/container
     */
    protected onEditButtonClick(container: any): void {
        const api = container.serviceData || {};

        // Prefer live container fields, then original API service payload
        this.facilityDataForEdit = {
            id: container.facilityTypeId || api.id,
            service_id: container.facilityTypeId || api.id,
            facilityName: container.facilityType || api.name || '',
            description: container.description ?? api.description ?? '',
            pricingType: container.pricingType || this.getPricingTypeFromUnitTypeId(api.unit_type_id) || api.unit?.name || '',
            location: container.location ?? api.location ?? '',
            capacity: container.capacity ?? api.capacity ?? '',
            status: container.isActive === false || api.status === false ? 'Inactive' : 'Active',
            service_taxes: container.service_taxes?.length ? container.service_taxes : (api.service_taxes || []),
            uploadedImages: container.uploadedImages?.length
                ? [...container.uploadedImages]
                : (api.media_assets || api.images || []),
            is_session_based: container.is_session_based ?? api.is_session_based ?? false,
            startDate: container.startDate || api.effective_start || api.effectiveStart || null,
            endDate: container.endDate || api.effective_end || api.effectiveEnd || null
        };

        // Open the drawer (populate happens via facilityDataForEdit input / ngOnChanges)
        this.open = true;
    }

    /**
     * Get the appropriate icon for pricing type
     */
    protected getPricingTypeIcon(pricingType: string): string {
        switch (pricingType) {
            case 'Time Based':
            case 'Hour Based':
                return '@tui.clock';
            case 'Per Unit':
                return '@tui.rows-4';
            case 'Flat Rate':
                return '@tui.tag';
            case 'Energy Consumption':
                return '@tui.zap';
            case 'Per Day':
                return '@tui.rows-4';
            default:
                return '@tui.clock';
        }
    }

    /**
     * Get the appropriate icon for unit type in Time Based pricing
     */
    protected getUnitTypeIcon(unitType: string): string {
        switch (unitType) {
            case 'Hourly':
                return '@tui.clock';
            case 'Flat Rate':
                return '@tui.tag';
            default:
                return '@tui.clock';
        }
    }

    // Create sample rows based on pricing type
    protected createSampleRowsForPricingType(pricingType: string): any[] {
        // Requirement: do not auto-create any tier rows for any pricing type.
        // Rows should be created only when the user clicks "Add Tiers".
        return [];
    }

    /**
     * Get facility initials from facility name
     * Example: "Snacks Counter" -> "SC", "Helmet Locker" -> "HL"
     */
    protected getFacilityInitials(facilityName: string): string {
        if (!facilityName) return '';

        const words = facilityName.trim().split(' ');
        if (words.length === 1) {
            // Single word - return first 2 letters
            return facilityName.substring(0, 2).toUpperCase();
        } else {
            // Multiple words - return first letter of each word
            return words.map(word => word.charAt(0)).join('').toUpperCase();
        }
    }

    /**
     * Get facility icon from vehicle icons service
     */
    protected getFacilityIcon(facilityName: string, facilityType?: string): any {
        return this.vehicleIconsService.getFacilityIcon(facilityName, facilityType);
    }

    /**
     * Get facility icon SVG code
     */
    protected getFacilityIconSvg(facilityName: string): string {
        const icon = this.getFacilityIcon(facilityName);
        return icon ? icon.svgCode : '';
    }

    /**
     * Get facility icon SVG code as SafeHtml
     */
    protected getFacilityIconSafeHtml(facilityName: string): SafeHtml {
        const icon = this.getFacilityIcon(facilityName);
        if (icon) {
            return this.sanitizer.bypassSecurityTrustHtml(icon.svgCode);
        }
        return this.sanitizer.bypassSecurityTrustHtml('');
    }

    /**
     * Get facility icon as data URL for img src
     */
    protected getFacilityIconDataUrl(facilityName: string, isDarkMode?: boolean, facilityType?: string): string {
        const darkMode = isDarkMode !== undefined ? isDarkMode : this.isDarkMode();
        return this.vehicleIconsService.getFacilityIconDataUrl(facilityName, darkMode, facilityType);
    }

    /**
     * Get facility icon with dynamic colors
     */
    protected getFacilityIconWithColors(facilityName: string, isDarkMode: boolean = false): any {
        const icon = this.getFacilityIcon(facilityName);
        if (icon) {
            return {
                ...icon,
                currentTheme: isDarkMode ? icon.darkTheme : icon.lightTheme
            };
        }
        return null;
    }

    /**
     * Get facility icon background color
     */
    public getFacilityIconBackgroundColor(facilityName: string, isDarkMode?: boolean, facilityType?: string): string {
        const darkMode = isDarkMode !== undefined ? isDarkMode : this.isDarkMode();
        return this.vehicleIconsService.getFacilityIconBackgroundColor(facilityName, darkMode, facilityType);
    }

    /**
     * Get facility icon text color
     */
    public getFacilityIconTextColor(facilityName: string, isDarkMode?: boolean, facilityType?: string): string {
        const darkMode = isDarkMode !== undefined ? isDarkMode : this.isDarkMode();
        return this.vehicleIconsService.getFacilityIconTextColor(facilityName, darkMode, facilityType);
    }


    /**
     * Get all available facility icons
     */
    protected getAllFacilityIcons(): any[] {
        return this.vehicleIconsService.facilitiesIcons;
    }

    /**
     * Debug method to log all available facility icons
     */
    protected logAllFacilityIcons(): void {
        const icons = this.getAllFacilityIcons();
    }

    // ==================== UNIT TYPES API METHODS ====================

    /**
     * Get available units from API
     */
    protected get availableUnits() {
        return () => this.apiUnitTypes;
    }

    /**
     * Load units from API
     */
    protected loadUnits(): void {
        this.isLoadingUnits.set(true);

        this.apiService.getUnits().subscribe({
            next: (response) => {
                if (response.success && response.data) {
                    this.apiUnitTypes = Array.isArray(response.data) ? response.data : [];
                    // Populate units and unitTypes from API data
                    this.units = this.apiUnitTypes.map(unit => unit.name || unit.label || unit.title || 'Unknown Unit');
                    this.unitTypes = ['Hourly', 'Flat Rate']; // Default unit types
                } else {
                    this.apiUnitTypes = [];
                    this.units = [];
                    this.unitTypes = [];
                }
                this.isLoadingUnits.set(false);
            },
            error: (error) => {
                this.apiUnitTypes = [];
                this.units = [];
                this.unitTypes = [];
                this.isLoadingUnits.set(false);
            }
        });
    }

    /**
     * Test unit API endpoint
     */
    protected testUnitApi(): void {
        this.loadUnits();
    }

    /**
     * Refresh services from API
     */
    protected refreshServices(): void {
        this.loadServices();
    }

    /**
     * Force refresh facility types from API data
     */
    protected forceRefreshFacilityTypes(): void {
        this.updateFacilityTypesFromApi();
        this.successMessage.set('Facility types refreshed from API data!');
        setTimeout(() => this.successMessage.set(''), 3000);
    }



    /**
     * Create containers from parking detail facilities automatically
     */
    protected createContainersFromParkingDetailFacilities(): void {
        // Filter only facility services
        const facilityServices = this.parkingDetailFacilities.filter(facility =>
            serviceTypeMatches(facility, 'facility')
        );

        if (facilityServices.length === 0) {
            return;
        }

        facilityServices.forEach((facility, index) => {
            // Check if container already exists
            const existingContainer = this.vehicleContainers().find(container =>
                container.facilityType === facility.name
            );

            if (!existingContainer) {
                const newContainerId = this.vehicleContainers().length + 1;

                const pricingType = this.resolveFacilityPricingType(facility);

                // Extract media assets from facility data
                let mediaAssets = facility.media_assets || [];

                // Note: Default images are handled in HTML template
                // Only use media_assets from API if available

                const newContainer = {
                    id: newContainerId,
                    facilityType: facility.name,
                    facilityTypeId: facility.id,
                    priceId: null,
                    isActive: facility.status !== false,
                    is_session_based: facility.is_session_based === true || facility.is_session_based === 'true',
                    pricingType: pricingType,
                    description: facility.description || '',
                    capacity: facility.capacity !== undefined && facility.capacity !== null
                        ? facility.capacity.toString()
                        : '0',
                    startDate: facility.effective_start || facility.effectiveStart || null,
                    endDate: facility.effective_end || facility.effectiveEnd || null,
                    uploadedImages: mediaAssets, // Use media_assets from API
                    tableRows: this.createSampleRowsForPricingType(pricingType),
                    service_taxes: facility.service_taxes || [], // Include service taxes for tax calculation
                    serviceData: facility,
                    created_at: facility.created_at || facility.createdAt || null,
                    createdAt: facility.createdAt || facility.created_at || null
                };

                // Add the new container
                this.vehicleContainers.update(containers => {
                    const updatedContainers = [...containers, newContainer];
                    return updatedContainers;
                });
            } else {
                this.vehicleContainers.update(containers => {
                    return containers.map(container => {
                        if (container.facilityType === facility.name) {
                            return {
                                ...container,
                                facilityTypeId: facility.id || container.facilityTypeId,
                                isActive: facility.status !== false,
                                is_session_based: facility.is_session_based === true || facility.is_session_based === 'true',
                                description: facility.description || container.description,
                                capacity: facility.capacity !== undefined && facility.capacity !== null
                                    ? facility.capacity.toString()
                                    : container.capacity,
                                startDate: facility.effective_start || facility.effectiveStart || container.startDate || null,
                                endDate: facility.effective_end || facility.effectiveEnd || container.endDate || null,
                                uploadedImages: facility.media_assets || container.uploadedImages,
                                service_taxes: facility.service_taxes || container.service_taxes || [],
                                serviceData: facility
                            };
                        }
                        return container;
                    });
                });
            }
        });

        // Force change detection
        setTimeout(() => {
            this.cdr.detectChanges();
        }, 100);

        // After containers are created, load their rates from service-rate API
        // this.loadAllServiceRatesForContainers();
    }

    /**
     * Test services API endpoint
     */
    protected testServicesApi(): void {
        this.loadServices();
    }

    /**
     * Test the specific existing services endpoint
     */
    protected testExistingServicesApi(): void {

        this.apiService.getExistingServices().subscribe({
            next: (response: any) => {
                // getExistingServices already uses extractData, so response should be the data array
                let servicesData: any[] = [];

                // Handle both response formats: extracted array or wrapped response
                if (Array.isArray(response)) {
                    // Direct array response (extracted by extractData)
                    servicesData = response;
                } else if (response && typeof response === 'object') {
                    // Fallback: if still wrapped, extract data
                    if (response.data && Array.isArray(response.data)) {
                        servicesData = response.data;
                    } else if (response.success && response.data && Array.isArray(response.data)) {
                        servicesData = response.data;
                    }
                }



                // Count facility services
                const facilityServices = servicesData.filter(service => serviceTypeMatches(service, 'facility'));

                this.successMessage.set(`API test successful! Found ${servicesData.length} total services, ${facilityServices.length} facility services. Check console for details.`);
                setTimeout(() => this.successMessage.set(''), 5000);
            },
            error: (error: any) => {
                this.errorMessage.set('Existing services API test failed: ' + (error.message || error.statusText || 'Unknown error'));
                setTimeout(() => this.errorMessage.set(''), 5000);
            }
        });
    }

    /**
     * Refresh facility types from parking detail API
     */
    protected refreshFacilityTypesFromParkingDetail(): void {
        this.loadFacilityTypesFromParkingDetail();
        this.notificationService.showInfo('Refreshing facility types from API...', 2000);
    }

    /**
     * Test parking detail API endpoint
     */
    protected testParkingDetailApi(): void {
        if (!this.parkingId) {
            this.notificationService.showError('No parking ID available for testing', 3000);
            return;
        }


        this.apiService.getParkingDetail(this.parkingId).subscribe({
            next: (response: any) => {
                this.successMessage.set('Parking detail API test successful!');
                this.errorMessage.set('');
                setTimeout(() => this.successMessage.set(''), 5000);
            },
            error: (error: any) => {
                this.errorMessage.set('Parking detail API test failed: ' + (error.message || error.statusText || 'Unknown error'));
                this.successMessage.set('');
            }
        });
    }

    /**
     * Reload parking ID from localStorage and refresh data
     */
    protected reloadParkingIdFromStorage(): void {
        const parkingIdFromStorage = sessionStorage.getItem('parking_id');
        if (parkingIdFromStorage) {
            this.parkingId = parkingIdFromStorage;
            this.successMessage.set('Parking ID reloaded from localStorage: ' + this.parkingId);

            // Refresh all data with new parking ID
            this.loadFacilityTypesFromParkingDetail();
            this.loadFacilityRateSlots();

            setTimeout(() => this.successMessage.set(''), 5000);
        } else {
            this.errorMessage.set('No parking_id found in localStorage');
        }
    }

    /**
     * Show localStorage parking_id value for debugging
     */
    protected showLocalStorageParkingId(): void {
        const parkingIdFromStorage = sessionStorage.getItem('parking_id');
        if (parkingIdFromStorage) {
            this.successMessage.set('localStorage parking_id: ' + parkingIdFromStorage);
        } else {
            this.errorMessage.set('No parking_id found in localStorage');
        }
        setTimeout(() => {
            this.successMessage.set('');
            this.errorMessage.set('');
        }, 3000);
    }

    /**
     * Show facility type services count for debugging
     */
    protected showFacilityTypeServicesCount(): void {
        const totalServices = this.apiServicesData.length;
        const facilityServices = this.apiServicesData.filter(service => serviceTypeMatches(service, 'facility'));
        const facilityServicesCount = facilityServices.length;

        this.successMessage.set(`Total services: ${totalServices}, Facility services: ${facilityServicesCount}`);

        setTimeout(() => {
            this.successMessage.set('');
        }, 5000);
    }

    /**
     * Extract and fetch unit type details from parking data
     */
    protected extractAndFetchUnitTypeDetails(parkingData: any): void {
        // Look for unit_type_id in parking data
        const unitTypeId = parkingData.unit_type_id;

        if (unitTypeId) {

            // Fetch unit type details from API
            this.fetchUnitTypeDetails(unitTypeId);
        } else {
        }
    }

    /**
     * Fetch unit type details from API using unit_type_id
     */
    protected fetchUnitTypeDetails(unitTypeId: string): void {

        this.apiService.getUnits().subscribe({
            next: (response: any) => {

                if (response && Array.isArray(response)) {
                    // Find the unit type with matching ID
                    const unitType = response.find((unit: any) => unit.id === unitTypeId);

                    if (unitType) {
                        this.displayUnitTypeDetails(unitType);
                    } else {
                        this.errorMessage.set(`Unit type not found for ID: ${unitTypeId}`);
                    }
                } else if (response && response.data && Array.isArray(response.data)) {
                    // Find the unit type with matching ID
                    const unitType = response.data.find((unit: any) => unit.id === unitTypeId);

                    if (unitType) {
                        this.displayUnitTypeDetails(unitType);
                    } else {
                        this.errorMessage.set(`Unit type not found for ID: ${unitTypeId}`);
                    }
                } else {
                    this.errorMessage.set('Invalid units API response format');
                }
            },
            error: (error: any) => {
                this.errorMessage.set('Error fetching unit type details: ' + (error.message || error.statusText || 'Unknown error'));
            }
        });
    }

    /**
     * Display unit type details
     */
    protected displayUnitTypeDetails(unitType: any): void {
        const unitInfo = {
            id: unitType.id,
            label: unitType.label,
            calculation_type: unitType.calculation_type,
            unit_label: unitType.unit_label,
            description: unitType.description
        };


        // Display the unit type details
        this.successMessage.set(`Unit Type: ${unitInfo.label} | Calculation: ${unitInfo.calculation_type} | Unit: ${unitInfo.unit_label} | Description: ${unitInfo.description}`);

        // Store unit type details for use in the component
        this.selectedUnit = unitInfo;
        this.unitTypeDetails = unitInfo;

        setTimeout(() => {
            this.successMessage.set('');
        }, 8000);
    }

    /**
     * Test fetching unit type details by specific ID
     */
    protected testFetchUnitTypeById(): void {
        // Test with the specific ID from the search results
        const testUnitTypeId = "55bd4dd9-6d88-4f08-abc6-8adeb55db82b";

        this.fetchUnitTypeDetails(testUnitTypeId);
    }





    /**
     * Clear status messages
     */
    protected clearStatusMessages(): void {
        this.successMessage.set('');
        this.errorMessage.set('');
    }

    /**
     * Clear and reload services
     */
    protected clearAndReloadServices(): void {
        this.apiServicesData = [];
        this.faclitiesTypes = [];
        this.availableFacilities.set([]);
        this.loadServices();
    }

    /**
     * Delete service from API
     */
    protected deleteService(serviceId: string): void {
        this.apiService.deleteService(serviceId).subscribe({
            next: (response: any) => {
                this.notificationService.showSuccess('Service deleted successfully!', 3000);

                // Remove the card from local containers
                this.vehicleContainers.update(containers =>
                    containers.filter(container => container.facilityTypeId !== serviceId)
                );

                // Reload services and facility rate slots to update the list
                this.loadServices();
                this.loadFacilityRateSlots();
            },
            error: (error: any) => {
                this.notificationService.showError('Failed to delete service. Please try again.', 4000);
            }
        });
    }

    /**
     * Delete selected service
     */
    protected deleteSelectedService(): void {
        if (!this.selectedCard) {
            this.notificationService.showError('Please select a service to delete.', 3000);
            return;
        }

        // Find the service in API data
        const service = this.apiServicesData.find(s => s.name === this.selectedCard?.name);
        if (!service) {
            this.notificationService.showError('Service not found.', 3000);
            return;
        }

        // Show confirmation dialog
        const data: TuiConfirmData = {
            content: `Are you sure you want to delete the service "${this.selectedCard.name}"? This action cannot be undone.`,
            yes: 'Yes, Delete',
            no: 'No, Keep it',
        };

        this.dialogs
            .open<boolean>(TUI_CONFIRM, {
                label: 'Confirm Service Deletion',
                size: 's',
                data,
            })
            .pipe(
                switchMap((response) => {
                    if (response) {
                        this.deleteService(service.id);
                        this.selectedCard = null;
                        this.vehicleSelectionForm.reset();
                        return of(null);
                    } else {
                        this.notificationService.showInfo('Deletion cancelled', 3000);
                        return of(null);
                    }
                })
            )
            .subscribe({
                next: (alertResponse) => {
                    // Handle response if needed
                },
                error: (error) => {
                    // Handle error if needed
                }
            });
    }

    /**
     * Load pricing types from API
     */
    protected loadPricingTypes(): void {
        // For now, use default pricing types until API endpoint is available
        this.pricingTypes = [
            {
                code: "time_based",
                label: "Time Based",
                calculation_type: "duration_in_hours or flat",
                unit_label: "hours or flat",
                description: "Time based consist of hourly or flat rate"
            },
            {
                code: "hourly",
                label: "Hour Based",
                calculation_type: "duration_in_hours",
                unit_label: "hours",
                description: "Charges based on how long the vehicle stays"
            },
            {
                code: "unit",
                label: "Per Unit",
                calculation_type: "quantity",
                unit_label: "unit",
                description: "Fixed charge per item or usage"
            },
            {
                code: "fixed",
                label: "Flat Rate",
                calculation_type: "flat",
                unit_label: "flat",
                description: "One-time charge regardless of usage"
            },
            {
                code: "energy",
                label: "Energy Consumption",
                calculation_type: "kwh",
                unit_label: "kWh",
                description: "Based on energy used (EV charging)"
            },
            {
                code: "day",
                label: "Per Day",
                calculation_type: "calendar_days",
                unit_label: "day",
                description: "Applies daily pricing logic"
            }
        ];
    }

    /**
     * Handle unit selection
     */
    protected onUnitSelect(unit: any): void {
        this.selectedUnit = unit;
    }

    /**
     * Get display name for unit
     */
    protected getUnitDisplayName(unit: any): string {
        return unit.name || unit.label || unit.title || 'Unknown Unit';
    }

    /**
     * Check if container has default images to show
     */
    protected hasDefaultImages(container: any): boolean {
        // Only show default images if there are no uploaded images from API
        if (container.uploadedImages && container.uploadedImages.length > 0) {
            return false; // Don't show default images if API has images
        }

        // Show default images for specific facility types only when no API images
        if (container.facilityType.toLowerCase().includes('refreshment')) {
            return true;
        }
        if (container.facilityType.toLowerCase().includes('ev') || container.facilityType.toLowerCase().includes('charging')) {
            return true;
        }
        if (container.facilityType.toLowerCase().includes('helmet') || container.facilityType.toLowerCase().includes('locker')) {
            return true;
        }
        // For other facilities, don't show default images
        return false;
    }

    /**
     * Get pricing type from service type
     */
    protected getPricingTypeFromServiceType(serviceType: string): string {
        switch (serviceType.toLowerCase()) {
            case 'facility':
                return 'Time Based';
            case 'energy':
                return 'Energy Consumption';
            case 'unit':
                return 'Per Unit';
            case 'flat':
                return 'Flat Rate';
            case 'day':
                return 'Per Day';
            default:
                return 'Time Based';
        }
    }


    /**
     * Load facility types from parking detail API
     */
    protected loadFacilityTypesFromParkingDetail(forceRefresh: boolean = false): void {
        if (!this.parkingId) {
            return;
        }

        this.isLoadingParkingDetail.set(true);

        this.apiService.getParkingDetail(this.parkingId, forceRefresh).subscribe({
            next: (response: any) => {
                if (response && (response.data || response)) {
                    const parkingData = response.data || response;

                    // Store all facilities from parking detail API
                    this.extractFacilitiesFromParkingData(parkingData);

                    // Extract facility types from parking data
                    this.extractFacilityTypesFromParkingData(parkingData);
                } else {
                    // Fallback to existing services data
                    this.updateFacilityTypesFromApi();
                }
                this.isLoadingParkingDetail.set(false);
            },
            error: (error: any) => {
                // Fallback to existing services data
                this.updateFacilityTypesFromApi();
                this.isLoadingParkingDetail.set(false);
            }
        });
    }

    /**
     * Extract all facilities from parking data
     */
    protected extractFacilitiesFromParkingData(parkingData: any): void {
        // Initialize empty array
        this.parkingDetailFacilities = [];

        // Check if parking data has facilities or services
        if (parkingData.facilities && Array.isArray(parkingData.facilities)) {
            this.parkingDetailFacilities = parkingData.facilities;
        } else if (parkingData.services && Array.isArray(parkingData.services)) {
            // Use services array if facilities array is not available
            this.parkingDetailFacilities = parkingData.services;
        }

        // Create containers for facility services automatically if data is available
        if (this.parkingDetailFacilities.length > 0) {
            this.createContainersFromParkingDetailFacilities();
        }
    }

    /**
     * Extract facility types from parking data
     */
    protected extractFacilityTypesFromParkingData(parkingData: any): void {

        // Only proceed if we don't have API data already
        if (this.faclitiesTypes.length > 0) {
            return;
        }

        // Initialize empty array only if no API data exists
        this.faclitiesTypes = [];

        // Get unit type ID from parking data
        const unitTypeId = parkingData.unit_type_id;

        // Check if parking data has facilities/services
        if (parkingData.facilities && Array.isArray(parkingData.facilities)) {
            // Extract facility names from facilities array, filter for facility type
            this.faclitiesTypes = parkingData.facilities
                .filter((facility: any) => serviceTypeMatches(facility, 'facility'))
                .map((facility: any) => ({
                    name: facility.name || facility.facilityType || 'Unknown Facility',
                    unit_type_id: unitTypeId // Store unit type ID for each facility
                }));
        } else if (parkingData.services && Array.isArray(parkingData.services)) {
            // Extract service names from services array, filter for facility type
            this.faclitiesTypes = parkingData.services
                .filter((service: any) => serviceTypeMatches(service, 'facility'))
                .map((service: any) => ({
                    name: service.name || service.serviceType || 'Unknown Service',
                    unit_type_id: unitTypeId // Store unit type ID for each facility
                }));
        } else if (parkingData.facilityTypes && Array.isArray(parkingData.facilityTypes)) {
            // Extract facility types directly, filter for facility type
            this.faclitiesTypes = parkingData.facilityTypes
                .filter((facilityType: any) => serviceTypeMatches(facilityType, 'facility'))
                .map((facilityType: any) => ({
                    name: facilityType.name || facilityType.type || 'Unknown Facility Type',
                    unit_type_id: unitTypeId // Store unit type ID for each facility
                }));
        } else {
            // If no specific facility data found in parking detail, populate from API existing services
            this.updateFacilityTypesFromApi();
            return;
        }

        // Filter based on service_access if restricted
        const serviceAccess = this.apiService.getServiceAccess();
        const user = this.apiService.getCurrentUser();
        const currentRole = (user?.current_role || user?.role?.name || user?.type || sessionStorage.getItem('current_role') || '').toLowerCase().trim();
        const isSuperOrAdmin = currentRole === 'super admin' || currentRole === 'admin' || currentRole === 'super_admin' || currentRole === 'vendor' || user?.email === 'system_admin@kormoan.com';

        if (!isSuperOrAdmin && serviceAccess && serviceAccess.facility !== undefined) {
            const hasFacilityAccess = serviceAccess.facility !== false &&
                (Array.isArray(serviceAccess.facility) ? serviceAccess.facility.length > 0 : !!serviceAccess.facility);

            if (!hasFacilityAccess) {
                this.faclitiesTypes = [];
            } else if (Array.isArray(serviceAccess.facility)) {
                const isAll = serviceAccess.facility.includes('all') || serviceAccess.facility.includes('*');
                if (!isAll) {
                    this.faclitiesTypes = this.faclitiesTypes.filter((f: any) =>
                        serviceAccess.facility.includes(f.id) ||
                        (f.service_id && serviceAccess.facility.includes(f.service_id)) ||
                        (f.exisitng_service_id && serviceAccess.facility.includes(f.exisitng_service_id)) ||
                        (f.name && serviceAccess.facility.includes(f.name))
                    );
                }
            }
        }

        // Update available facilities for UI
        const availableFacilityUsers = this.faclitiesTypes.map(facilityType =>
            new User(facilityType.name, null)
        );
        this.availableFacilities.set(availableFacilityUsers);


        // Extract and fetch unit type details if available
        this.extractAndFetchUnitTypeDetails(parkingData);
    }

    /**
     * Load rates for all facility containers from service-rate API and populate table rows
     */
    protected loadAllServiceRatesForContainers(): void {
        const containers = this.vehicleContainers();
        if (!containers || containers.length === 0) return;

        containers.forEach((container: any) => {
            const serviceId = container.facilityTypeId || container?.serviceData?.id || container.id;
            if (!serviceId) return;

            this.apiService.getAllRatesByServiceId(serviceId, {
                parkingId: this.parkingId || undefined,
                orgId: this.apiService.getCurrentOrganizationId() || undefined,
            }).subscribe({
                next: (res: any) => {
                    // Attach service taxes onto the container so tax math can use them
                    if (res && res.service && Array.isArray(res.service.service_taxes)) {
                        container.service_taxes = res.service.service_taxes;
                    }
                    const rates: any[] = Array.isArray(res?.rates) ? res.rates : [];
                    if (!rates.length) return;

                    // Sort rates by min_duration in ascending order
                    rates.sort((a: any, b: any) => (Number(a.min_duration) || 0) - (Number(b.min_duration) || 0));

                    const rows = rates.map((rate: any, i: number) => ({
                        id: i + 1,
                        duration: rate.tier_name || `${rate.min_duration}h - ${rate.max_duration}h`,
                        minHours: `${rate.min_duration}h`,
                        maxHours: `${rate.max_duration}h`,
                        price: Number(rate.price) || 0,
                        taxAmount: 0,
                        taxPercent: 0,
                        withTax: Number(rate.price) || 0,
                        type: rate.unitLabel || 'Hourly',
                        unit: rate.unitLabel || 'Hourly',
                        unitType: rate.unitLabel || 'Hourly',
                        description: rate.description || '',
                        tierName: rate.tier_name || `${rate.min_duration}h - ${rate.max_duration}h`,
                        available: (rate.quantity_limit !== undefined && rate.quantity_limit !== null) ? rate.quantity_limit : '',
                        priority: rate.priority || i + 1,
                        isEditMode: false,
                        rateId: rate.id,
                        form: new FormGroup({
                            durationField: new FormControl({ value: rate.tier_name || `${rate.min_duration}h - ${rate.max_duration}h`, disabled: true }),
                            minHoursField: new FormControl({ value: `${rate.min_duration}h`, disabled: true }),
                            maxHoursField: new FormControl({ value: `${rate.max_duration}h`, disabled: true }),
                            priceField: new FormControl({ value: Number(rate.price) || 0, disabled: true }),
                            withTaxField: new FormControl({ value: Number(rate.price) || 0, disabled: true }),
                            typeField: new FormControl({ value: rate.unitLabel || 'Hourly', disabled: true }),
                            unitTypeField: new FormControl({ value: rate.unitLabel || 'Hourly', disabled: true }),
                            tierNameField: new FormControl({ value: rate.tier_name || `${rate.min_duration}h - ${rate.max_duration}h`, disabled: true }),
                            availableField: new FormControl({ value: (rate.quantity_limit !== undefined && rate.quantity_limit !== null) ? rate.quantity_limit : '', disabled: true }),
                            descriptionField: new FormControl({ value: rate.description || '', disabled: true })
                        })
                    }));

                    container.tableRows = rows;
                    this.vehicleContainers.update(cs => {
                        const clone = [...cs];
                        const idx = clone.findIndex(c => c.id === container.id);
                        if (idx >= 0) clone[idx] = container;
                        return clone;
                    });
                    setTimeout(() => this.cdr.detectChanges());
                },
                error: (error) => {
                    // ignore per-container failures
                }
            });
        });
    }

    /**
     * Reload facility rates for a specific container from API
     */
    protected reloadContainerRates(container: any): void {
        const serviceId = container.facilityTypeId || container?.serviceData?.id || container.id;
        if (!serviceId) return;

        this.apiService.getAllRatesByServiceId(serviceId, {
            parkingId: this.parkingId || undefined,
            orgId: this.apiService.getCurrentOrganizationId() || undefined,
        }).subscribe({
            next: (res: any) => {
                // Attach service taxes onto the container so tax math can use them
                if (res && res.service && Array.isArray(res.service.service_taxes)) {
                    container.service_taxes = res.service.service_taxes;
                }
                const rates: any[] = Array.isArray(res?.rates) ? res.rates : [];

                // Sort rates by min_duration in ascending order
                if (rates && rates.length > 0) {
                    rates.sort((a: any, b: any) => (Number(a.min_duration) || 0) - (Number(b.min_duration) || 0));
                }

                // Clear existing table rows
                container.tableRows = [];

                let rows: any[] = [];

                // Process each rate and add to container
                if (rates.length > 0) {
                    rows = rates.map((rate: any, i: number) => ({
                        id: i + 1,
                        duration: rate.tier_name || `${rate.min_duration}h - ${rate.max_duration}h`,
                        minHours: `${rate.min_duration}h`,
                        maxHours: `${rate.max_duration}h`,
                        price: Number(rate.price) || 0,
                        taxAmount: 0,
                        taxPercent: 0,
                        withTax: Number(rate.price) || 0,
                        type: rate.unitLabel || 'Hourly',
                        unit: rate.unitLabel || 'Hourly',
                        unitType: rate.unitLabel || 'Hourly',
                        description: rate.description || '',
                        tierName: rate.tier_name || `${rate.min_duration}h - ${rate.max_duration}h`,
                        available: (rate.quantity_limit !== undefined && rate.quantity_limit !== null) ? rate.quantity_limit : '',
                        priority: rate.priority || i + 1,
                        isEditMode: false,
                        rateId: rate.id,
                        form: new FormGroup({
                            durationField: new FormControl({ value: rate.tier_name || `${rate.min_duration}h - ${rate.max_duration}h`, disabled: true }),
                            minHoursField: new FormControl({ value: `${rate.min_duration}h`, disabled: true }),
                            maxHoursField: new FormControl({ value: `${rate.max_duration}h`, disabled: true }),
                            priceField: new FormControl({ value: Number(rate.price) || 0, disabled: true }),
                            withTaxField: new FormControl({ value: Number(rate.price) || 0, disabled: true }),
                            typeField: new FormControl({ value: rate.unitLabel || 'Hourly', disabled: true }),
                            unitTypeField: new FormControl({ value: rate.unitLabel || 'Hourly', disabled: true }),
                            tierNameField: new FormControl({ value: rate.tier_name || `${rate.min_duration}h - ${rate.max_duration}h`, disabled: true }),
                            availableField: new FormControl({ value: (rate.quantity_limit !== undefined && rate.quantity_limit !== null) ? rate.quantity_limit : '', disabled: true }),
                            descriptionField: new FormControl({ value: rate.description || '', disabled: true })
                        })
                    }));
                }

                container.tableRows = rows;

                // Update the signal to trigger UI refresh
                this.vehicleContainers.update(cs => {
                    return cs.map(c => {
                        if (c.id === container.id) {
                            // Create a new object reference to ensure change detection works
                            return { ...c, tableRows: rows };
                        }
                        return c;
                    });
                });
                setTimeout(() => this.cdr.detectChanges());
            },
            error: (error) => {
                console.error('Failed to reload container rates:', error);
            }
        });
    }

    /**
     * Save row in container - handles both create and update
     */
    protected saveRowInContainer(container: any, row: any): void {
        if (!this.canSaveRow(row)) {
            row.validationAttempted = true;
            this.notificationService.showError('Price is required to save this tier');
            this.vehicleContainers.update(containers => [...containers]);
            this.cdr.detectChanges();
            return;
        }

        row.validationAttempted = false;
        this.performSaveRow(container, row);
    }

    /**
     * Show save confirmation dialog
     */
    protected showSaveConfirmDialog(container: any, row: any): void {
        const data: TuiConfirmData = {
            content: 'Are you sure you want to save this pricing tier?',
            yes: 'Save',
            no: 'Cancel',
        };

        this.dialogs
            .open<boolean>(TUI_CONFIRM, {
                label: 'Confirm Save',
                size: 's',
                data,
            })
            .subscribe({
                next: (response) => {
                    if (response) {
                        this.performSaveRow(container, row);
                    }
                }
            });
    }

    /**
     * Default rate description when user leaves description empty (also sent to API).
     */
    protected getDefaultRateDescription(container: any, row: any): string {
        const pricingType = String(container?.pricingType || '').trim();
        const maxHours = this.getNumericValue(row?.maxHours);

        if (pricingType === 'Time Based' || pricingType === 'Hour Based') {
            if (this.isFlatUnitType(row)) {
                return `Flat rate for Max ${maxHours} Hours.`;
            }
            return `For Max ${maxHours} Hours.`;
        }

        if (pricingType === 'Flat Rate') {
            return 'Flat rate charge.';
        }

        if (pricingType === 'Per Unit') {
            return 'Charge per unit.';
        }

        if (pricingType === 'Per Day') {
            return 'Charge per day.';
        }

        if (pricingType === 'Energy Consumption') {
            return 'Charge based on energy consumption.';
        }

        return '';
    }

    /**
     * Perform the actual save operation after user confirmation
     */
    protected performSaveRow(container: any, row: any): void {
        // Set saving state for this specific row
        this.savingRowId.set(row.id);

        // Hourly / Flat: min 0h only when no Flat in list. Max hours is whatever the user entered.
        if (this.hasFixedMinHours(row, container)) {
            row.minHours = '0h';
        }
        if (this.isFlatUnitType(row) && !row.maxHours && row.maxHours !== 0) {
            row.maxHours = '24h';
        }

        // Default description when user left it empty (sent to API + shown on row)
        if (!row.description || String(row.description).trim() === '') {
            row.description = this.getDefaultRateDescription(container, row);
        }

        // Preserve user-entered duration, only auto-generate if completely empty
        if (!row.duration || row.duration.trim() === '') {
            const defaultDuration = this.calculateDuration(row.minHours, row.maxHours);
            row.duration = defaultDuration;
        }
        // User-entered values (like "helmet") are preserved

        // Prepare payload in the requested format
        const orgId = this.apiService.getCurrentOrganizationId();
        const serviceId = container?.facilityTypeId || container?.serviceData?.id || container?.id || null;

        // Determine effective unit label based on pricing type
        // If pricing is Flat Rate, FORCE unitLabel to 'Flat Rate' to prevent 'Hourly' from carrying over
        const getUnitLabel = (currentRowUnitType: string) => {
            if (container.pricingType === 'Flat Rate') {
                return 'Flat Rate';
            }
            return currentRowUnitType || 'Hourly';
        };

        const resolveDescription = (tableRow: any): string => {
            const typed = tableRow.description ? String(tableRow.description).trim() : '';
            if (typed) return typed;
            return this.getDefaultRateDescription(container, tableRow);
        };

        // Determine if this is create or update based on rateId
        const isUpdate = !!row.rateId;

        let payload: any;

        if (isUpdate) {
            // For updates: Send all existing rates along with the edited rate
            const allRatesWithIds = container.tableRows
                .filter((r: any) => !!r.rateId)
                .map((tableRow: any, index: number) => {
                    const priceNum = Number(tableRow.price) || 0;
                    const minDur = this.getNumericValue(tableRow.minHours);
                    const maxDur = this.getNumericValue(tableRow.maxHours);
                    const tierName = tableRow.tierName || tableRow.duration || `${minDur}h - ${maxDur}h`;
                    const unitLabel = getUnitLabel(tableRow.unitType);

                    const rateObj: any = {
                        id: tableRow.rateId,
                        tier_name: tierName,
                        description: resolveDescription(tableRow),
                        price: priceNum,
                        unitLabel: unitLabel,
                        min_duration: minDur,
                        max_duration: maxDur,
                        priority: tableRow.priority || (index + 1),
                        status: (container.isActive ? 'active' : 'inactive'),
                    };
                    if (tableRow.available !== null && tableRow.available !== undefined && tableRow.available !== '') {
                        rateObj.quantity_limit = Number(tableRow.available);
                    }
                    return rateObj;
                });

            payload = {
                service_id: serviceId,
                parking_id: this.parkingId,
                org_id: orgId || '',
                rates: allRatesWithIds
            };
        } else {
            // For creates: Only send the new rate
            const newRateObj: any = {
                tier_name: row.tierName || row.duration || `${this.getNumericValue(row.minHours)}h - ${this.getNumericValue(row.maxHours)}h`,
                description: resolveDescription(row),
                price: Number(row.price) || 0,
                unitLabel: getUnitLabel(row.unitType),
                min_duration: this.getNumericValue(row.minHours),
                max_duration: this.getNumericValue(row.maxHours),
                priority: row.priority || row.id,
                status: (container.isActive ? 'active' : 'inactive'),
            };
            if (row.available !== null && row.available !== undefined && row.available !== '') {
                newRateObj.quantity_limit = Number(row.available);
            }

            payload = {
                service_id: serviceId,
                parking_id: this.parkingId,
                org_id: orgId || '',
                rates: [newRateObj]
            };
        }

        // Validate data before sending
        if (!serviceId) {
            this.errorMessage.set('Missing facility type ID. Please try again.');
            this.savingRowId.set(null);
            return;
        }

        if (!payload.rates || payload.rates.length === 0) {
            this.errorMessage.set('No pricing tiers to save.');
            this.savingRowId.set(null);
            return;
        }

        if (isUpdate) {
            // Update existing pricing using PUT method
            this.apiService.updateServiceRate(payload).subscribe({
                next: (response) => {
                    this.notificationService.showSuccess('Facility pricing updated successfully!', 3000);

                    // Clear saving state
                    this.savingRowId.set(null);

                    // Refresh data from API to get the latest state for this container only
                    this.reloadContainerRates(container);

                    // Re-verify setup completion status
                    this.setupGuideService.checkSetupCompletion().subscribe();
                },
                error: (error) => {
                    this.notificationService.showError('Failed to update facility pricing. Please try again.', 4000);
                    this.savingRowId.set(null);
                }
            });
        } else {
            // Create new pricing
            this.apiService.createServiceRates(payload).subscribe({
                next: (response) => {
                    this.notificationService.showSuccess('Facility pricing created successfully!', 3000);

                    // Clear saving state
                    this.savingRowId.set(null);

                    // Refresh data from API to get the latest state for this container only
                    this.reloadContainerRates(container);

                    // Re-verify setup completion status
                    this.setupGuideService.checkSetupCompletion().subscribe();
                },
                error: (error) => {
                    this.notificationService.showError('Failed to create facility pricing. Please try again.', 4000);
                    this.savingRowId.set(null);
                }
            });
        }
    }

    /**
     * Update row with API response data
     */
    private updateRowWithApiResponse(row: any, apiData: any): void {
        if (apiData.rateId) {
            row.rateId = apiData.rateId;
        }
        if (apiData.price) {
            row.price = apiData.price;
        }
        if (apiData.tax) {
            row.taxAmount = apiData.tax;
        }
        if (apiData.total) {
            row.withTax = parseFloat(apiData.total);
        }
    }

    /**
     * Refresh container data after save
     */
    private refreshContainerData(container: any): void {
        // Trigger change detection for the container
        this.vehicleContainers.update(containers => [...containers]);

        // Force change detection
        setTimeout(() => {
            this.cdr.detectChanges();
        }, 100);
    }

    /**
     * Calculate total tax amount for a price
     */
    private calculateTotalTaxAmount(price: number, container: any): number {
        if (!container.service_taxes || !Array.isArray(container.service_taxes)) {
            return 0;
        }

        let totalTax = 0;
        container.service_taxes.forEach((serviceTax: any) => {
            const tax = serviceTax.taxes;
            if (tax && tax.type === 'percentage' && tax.rate_percent) {
                const taxRate = parseFloat(tax.rate_percent) / 100;
                totalTax += price * taxRate;
            } else if (tax && tax.type === 'amount' && tax.rate_amount) {
                totalTax += parseFloat(tax.rate_amount);
            }
        });

        return totalTax;
    }

    /**
     * Check if row can be saved
     */
    protected canSaveRow(row: any): boolean {
        return row.price && parseFloat(row.price) > 0;
    }

    /**
     * Calculate price with tax for display
     */
    protected calculatePriceWithTax(row: any, container: any): number {
        const basePrice = parseFloat(row.price) || 0;
        const taxAmount = this.calculateTotalTaxAmount(basePrice, container);
        return basePrice + taxAmount;
    }

    /**
       * Get container tax display text
       */
    protected getContainerTax(container: any): string {
        // 1. First check rich service_taxes sources
        const serviceTaxes = (Array.isArray(container.service_taxes) && container.service_taxes.length > 0)
            ? container.service_taxes
            : (Array.isArray(container.serviceTaxes) && container.serviceTaxes.length > 0)
                ? container.serviceTaxes
                : (Array.isArray(container.rawService?.service_taxes) && container.rawService.service_taxes.length > 0)
                    ? container.rawService.service_taxes
                    : null;

        if (serviceTaxes && serviceTaxes.length > 0) {
            const taxes = serviceTaxes.map((serviceTax: any) => {
                const tax = serviceTax?.taxes || serviceTax || {};
                const name = (tax.name || tax.tax_name || '').toString().trim().toUpperCase();
                const type = (tax.type || '').toString().toLowerCase();
                const ratePercent = tax.rate_percent ?? tax.percentage;
                const rateAmount = tax.rate_amount ?? tax.rate_fixed ?? tax.amount;

                if (type === 'percentage' || (ratePercent !== null && ratePercent !== undefined && ratePercent !== '')) {
                    const percentage = parseFloat(ratePercent || 0).toString();
                    return name ? `${name} (${percentage}%)` : `(${percentage}%)`;
                } else if (type === 'amount' || (rateAmount !== null && rateAmount !== undefined && rateAmount !== '')) {
                    return name ? `${name} (${rateAmount})` : `(${rateAmount})`;
                }
                return name;
            }).filter((t: string) => t && t.trim().length > 0);

            if (taxes.length > 0) return taxes.join(', ');
        }

        // 2. Direct string tax
        if (typeof container.tax === 'string' && container.tax.trim().length > 0) {
            return container.tax.trim();
        }

        // 3. Direct taxValue (string or non-empty string array)
        if (container.taxValue) {
            if (Array.isArray(container.taxValue)) {
                const names = container.taxValue.filter((n: any) => typeof n === 'string' && n.trim().length > 0);
                if (names.length > 0) {
                    return names.join(', ');
                }
            } else if (typeof container.taxValue === 'string' && container.taxValue.trim().length > 0) {
                return container.taxValue.trim();
            }
        }

        // If no tax information available, return default
        return 'No tax';
    }

    /**
     * Get unit type label by ID
     */
    protected getUnitTypeLabelById(unitTypeId?: string): string {
        if (!unitTypeId) return 'Hourly';
        const label = this.unitTypeIdToLabel.get(unitTypeId);
        return label || this.getPricingTypeFromUnitTypeId(unitTypeId);
    }

    /**
     * Drag and drop handlers
     */
    protected onDragStart(event: DragEvent, row: any, index: number): void {
        try {
            event.dataTransfer?.setData('text/plain', String(index));
            if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
        } catch { }
    }

    protected onDragOver(event: DragEvent): void {
        // Allow dropping
        event.preventDefault();
        try {
            if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
        } catch { }
    }

    protected onDrop(event: DragEvent, row: any, dropIndex: number, container: any): void {
        event.preventDefault();
        const data = event.dataTransfer?.getData('text/plain');
        const sourceIndex = data != null ? Number(data) : NaN;
        if (!Number.isFinite(sourceIndex)) return;
        if (!container || !Array.isArray(container.tableRows)) return;

        const updated = [...container.tableRows];
        if (sourceIndex < 0 || sourceIndex >= updated.length) return;
        const [moved] = updated.splice(sourceIndex, 1);
        const targetIndex = Math.max(0, Math.min(dropIndex, updated.length));
        updated.splice(targetIndex, 0, moved);

        // Re-number priorities after reorder
        updated.forEach((r: any, i: number) => {
            r.priority = i + 1;
        });

        container.tableRows = updated;
        // Trigger change detection for the container
        this.vehicleContainers.update(containers => [...containers]);
        setTimeout(() => this.cdr.detectChanges(), 0);

        // Persist new priorities to backend using service/rate API (same as vehicle-rate)
        try {
            const orgId = this.apiService.getCurrentOrganizationId();
            const serviceId = container?.facilityTypeId || container?.serviceData?.id || container?.id || null;
            if (!serviceId) return;

            const payload: any = {
                service_id: serviceId,
                parking_id: this.parkingId,
                org_id: orgId || '',
                // Only send persisted rows (those with rateId)
                rates: updated
                    .filter((r: any) => !!r.rateId)
                    .map((r: any, idx: number) => {
                        const minDur = this.getNumericValue(r.minHours);
                        const maxDur = this.getNumericValue(r.maxHours);
                        const tierName = r.duration || r.tierName || `${minDur}h - ${maxDur}h`;
                        const unitLabel = r.unitType || 'Hourly';
                        const rateObj: any = {
                            id: r.rateId,
                            tier_name: tierName,
                            description: r.description || '',
                            price: Number(r.price) || 0,
                            unitLabel: unitLabel,
                            min_duration: minDur,
                            max_duration: maxDur,
                            priority: r.priority || idx + 1,
                            status: (container.isActive ? 'active' : 'inactive'),
                        };
                        if (r.available !== null && r.available !== undefined && r.available !== '') {
                            rateObj.quantity_limit = Number(r.available);
                        }
                        return rateObj;
                    })
            };

            // Call update API to persist ordering
            this.apiService.updateServiceRate(payload).subscribe({
                next: () => this.notificationService.showSuccess('Priority updated.', 2000),
                error: () => this.notificationService.showError('Failed to update priority.', 3000),
            });
        } catch { }
    }

    // File upload validator
    private maxFilesLength(maxLength: number): ValidatorFn {
        return (control: any) => {
            const files = control.value || [];
            const newFilesCount = files.length;
            const existingFilesCount = this.existingImages ? this.existingImages.length : 0;
            const totalFiles = newFilesCount + existingFilesCount;

            return totalFiles > maxLength ? { maxLength: { requiredLength: maxLength, actualLength: totalFiles } } : null;
        };
    }

    private maxFileSize(maxSize: number): ValidatorFn {
        return (control: any) => {
            const files: File[] = control.value || [];
            const largeFiles = files.filter(file => file?.size > maxSize);
            return largeFiles.length > 0
                ? { maxSize: { requiredSize: maxSize, actualSize: largeFiles[0].size } }
                : null;
        };
    }

    protected getTotalFilesCount(): number {
        const newFilesCount = this.fileControl.value ? this.fileControl.value.length : 0;
        const existingFilesCount = this.existingImages ? this.existingImages.length : 0;
        return newFilesCount + existingFilesCount;
    }

    protected getFileUrl(file: File): string {
        if (!this.fileUrlCache.has(file)) {
            const url = URL.createObjectURL(file);
            this.fileUrlCache.set(file, url);
        }
        return this.fileUrlCache.get(file) || '';
    }

    protected onReject(files: readonly File[]): void {
        this.rejected = [...this.rejected, ...files];
        const hasOversized = files.some(file => (file as File)?.size > this.maxImageSizeBytes);
        if (hasOversized) {
            this.uploadErrorMessage =
                'Each image must be 2MB or smaller. Please compress the image or choose a smaller file.';
        }
        this.cdr.markForCheck();
    }

    protected onRemove(file: File): void {
        const currentFiles = this.fileControl.value || [];
        this.fileControl.setValue(currentFiles.filter(f => f !== file));
        // Clean up blob URL
        if (this.fileUrlCache.has(file)) {
            const url = this.fileUrlCache.get(file);
            if (url) {
                URL.revokeObjectURL(url);
            }
            this.fileUrlCache.delete(file);
        }
        this.uploadErrorMessage = '';
    }

    protected removeExistingImage(index: number): void {
        this.existingImages.splice(index, 1);
        this.fileControl.updateValueAndValidity();
        this.uploadErrorMessage = '';
    }

    protected uploadImages(container: any): void {
        // Clear any previous error message
        this.uploadErrorMessage = '';
        this.isUploadingImages = false;

        // Store the current container
        this.currentFacilityContainer = container;

        // Load existing images before opening dialog
        this.existingImages = container.uploadedImages ? [...container.uploadedImages] : [];

        // Reset file control and clean up blob URLs
        const currentFiles = this.fileControl.value || [];
        currentFiles.forEach(file => {
            if (this.fileUrlCache.has(file)) {
                const url = this.fileUrlCache.get(file);
                if (url) {
                    URL.revokeObjectURL(url);
                }
                this.fileUrlCache.delete(file);
            }
        });
        this.fileControl.setValue([]);
        this.rejected = [];

        // Open the upload dialog — close without uploading (X / backdrop)
        this.dialogs.open(this.uploadDialog || '', {
            size: 'm',
            closeable: true,
        }).subscribe();
    }

    protected confirmUpload(observer: { complete: () => void }): void {
        if (this.isUploadingImages) {
            return;
        }

        this.uploadErrorMessage = '';
        const container = this.currentFacilityContainer;
        const files = this.fileControl.value || [];
        const originalCount = container?.uploadedImages?.length || 0;
        const hasChanges = files.length > 0 || this.existingImages.length !== originalCount;

        if (!container || !hasChanges) {
            observer.complete();
            return;
        }

        const oversized = files.filter(file => file.size > this.maxImageSizeBytes);
        if (oversized.length > 0) {
            this.rejected = [...this.rejected, ...oversized];
            this.fileControl.setValue(files.filter(file => file.size <= this.maxImageSizeBytes));
            this.uploadErrorMessage =
                'Each image must be 2MB or smaller. Please compress the image or choose a smaller file.';
            this.cdr.detectChanges();
            return;
        }

        this.performUpload(files, container, observer);
    }

    private getUploadErrorMessage(error: any): string {
        if (error?.status === 413) {
            return 'Upload failed: image is too large (max 2MB per image). Please compress your photos or upload fewer/smaller files and try again.';
        }

        const apiMessage =
            error?.error?.message ||
            error?.error?.error ||
            error?.error?.data?.message ||
            (typeof error?.error === 'string' ? error.error : null) ||
            error?.message;

        if (apiMessage && typeof apiMessage === 'string' && apiMessage.trim()) {
            return apiMessage.trim();
        }

        return 'Failed to upload images. Please try again.';
    }

    private performUpload(files: File[], container: any, observer?: { complete: () => void }): void {
        // Try multiple locations for service ID
        const serviceId = container?.facilityTypeId ||
            container?.serviceData?.id ||
            container?.id ||
            null;

        if (!serviceId) {
            console.error('Service ID not found in container:', container);
            this.uploadErrorMessage = 'Service ID not found. Please save the facility first.';
            this.notificationService.showError(this.uploadErrorMessage, 3000);
            this.cdr.detectChanges();
            return;
        }

        const orgId = this.apiService.getCurrentOrganizationId();
        const parkingId = this.apiService.getCurrentParkingId();

        console.log('Uploading images for service ID:', serviceId);
        console.log('New files:', files.length);
        console.log('Existing images to preserve:', this.existingImages.length);

        // Use updateServiceMediaAssets to preserve existing images and upload new ones
        if (files.length > 0 || this.existingImages.length > 0) {
            this.isUploadingImages = true;
            this.uploadErrorMessage = '';
            this.cdr.detectChanges();

            this.apiService.updateServiceMediaAssets({
                service_id: serviceId,
                org_id: orgId || undefined,
                parking_id: parkingId || undefined,
                files: files,
                existing_urls: this.existingImages // Pass existing images to preserve them
            }).then((response: any) => {
                this.isUploadingImages = false;
                this.uploadErrorMessage = '';
                this.notificationService.showSuccess('Images uploaded successfully', 2000);

                // Get updated media URLs from response
                const updatedMediaUrls = response?.data?.mediaUrls || response?.mediaUrls || [];

                // Create optimistic list for immediate UI update
                const newFileUrls = files.map(f => URL.createObjectURL(f));
                const optimisticImages = [...this.existingImages, ...newFileUrls];

                // Use API response if valid, otherwise fallback to optimistic list
                const finalImages = (updatedMediaUrls && updatedMediaUrls.length > 0)
                    ? updatedMediaUrls
                    : optimisticImages;

                // Immediately update the container's uploadedImages
                if (this.currentFacilityContainer) {
                    this.vehicleContainers.update(containers => {
                        return containers.map(container => {
                            if (container.id === this.currentFacilityContainer.id ||
                                container.facilityType === this.currentFacilityContainer.facilityType) {
                                return {
                                    ...container,
                                    uploadedImages: finalImages
                                };
                            }
                            return container;
                        });
                    });
                }

                // Clear file control and clean up blob URLs
                const filesToClean = this.fileControl.value || [];
                filesToClean.forEach(file => {
                    if (this.fileUrlCache.has(file)) {
                        const url = this.fileUrlCache.get(file);
                        if (url) {
                            URL.revokeObjectURL(url);
                        }
                        this.fileUrlCache.delete(file);
                    }
                });
                this.fileControl.setValue([]);
                this.rejected = [];
                this.existingImages = [];

                // Close dialog only after successful upload
                observer?.complete();

                // Force change detection
                this.cdr.detectChanges();
            }).catch((error) => {
                this.isUploadingImages = false;
                this.uploadErrorMessage = this.getUploadErrorMessage(error);
                this.notificationService.showError(this.uploadErrorMessage, 5000);
                // Keep modal open so user can retry
                this.cdr.detectChanges();
            });
        } else {
            // No files to upload and no existing images to preserve
            this.fileControl.setValue([]);
            this.rejected = [];
            this.existingImages = [];
            observer?.complete();
            this.cdr.detectChanges();
        }
    }
}
